/**
 * MAESTRO Preis-Engine (Spec 02, D "Engine") — deterministisch, ausschliesslich Integer-Cents.
 *
 * EINE Quelle der Wahrheit fuer UI-Vorschau, Public-Offer, LexOffice-Export und Stripe. Der
 * Neubau ersetzt die drei parallel rechnenden Alt-Implementierungen (Client/Anzeige/Edge) und
 * die dortigen Geld-Bugs (Float-Euro, Faktor-N bei per_event, rueckwaerts geratene USt).
 *
 * Leitplanken:
 *  - Geld immer Integer-Cent, Brutto. Mengen als Tausendstel (qty_milli), Division erst NACH
 *    der Multiplikation, Rundung pro Zeile auf Cent — kein IEEE-754 im Geldpfad.
 *  - USt je Satz aus Brutto: vat = gross - round(gross / (1 + rate)). Exakt dieselbe Formel
 *    im LexOffice-Export.
 *  - Rabatt/Anpassung je USt-Bucket per Largest-Remainder verteilt (cent-exakt, deterministisch:
 *    groesster Restanteil zuerst, dann rate_bp aufsteigend).
 */

// B2/B3: die Pro-Person-Formel wohnt in pro-person.ts, damit Angebotsseite, Angebotsmail und Engine dieselbe benutzen.
import { proPersonCents } from "./pro-person";
import { teileSpeisenGetraenke } from "./abrechnung";

export type PriceMode = "per_person" | "per_unit" | "flat";

/** Sektions-Konstante fuer die (max. eine) Preisanpassungs-Zeile je Option. */
export const ADJUSTMENT_SECTION = "adjustment";

export interface EngineItem {
  id?: string;
  section: string; // 'menu'|'drinks'|'equipment'|'staff'|'other'|'adjustment'
  priceMode: PriceMode;
  /** Menge in Tausendstel (1000 = 1, 330 = 0,33). */
  qtyMilli: number;
  /** Brutto-Einzelpreis in Cent. Bei der Anpassungszeile ggf. negativ. */
  unitPriceCents: number;
  /** MwSt-Satz in Basispunkten (1900 = 19 %, 700 = 7 %, 0 = steuerfrei/§19). */
  vatRateBp: number;
  discountable?: boolean; // default true
  /**
   * H-21: optionale Position ("auf Wunsch zubuchbar"). Wird in lineTotals ausgewiesen
   * (Anzeige-Preis), zaehlt aber NICHT in Zwischensumme, Rabattbasis, USt-Buckets oder
   * Endpreis — der Kunde darf nicht fuer etwas zahlen, das als optional ausgewiesen ist.
   */
  isOptional?: boolean;
  /**
   * Migration 722 (Antoine 14.10.2026): „zzgl.“ in einer Sektion mit Pauschale. Die Zeile ist
   * fest bestellt und zaehlt ZUSAETZLICH zur Pauschale — Zwischensumme, USt nach eigenem Satz und
   * Rabattbasis (anders als „zubuchbar“). Ausserhalb einer Pauschale ohne Wirkung.
   */
  surcharge?: boolean | null;
  /**
   * Migration 751 (Antoine 26.10.2026): eine ZUBUCHUNG (`isOptional`), die fuer die gebuchten Gaeste
   * das Hauptmenue ERSETZT (z. B. „vegetarisches Menue“). Ohne Wirkung, solange die Zeile nicht
   * gebucht ist; gebucht senkt sie die Gaestezahl des Hauptmenues (siehe `ersetzteGaesteAus`).
   */
  addonErsetztHauptmenue?: boolean | null;
  /**
   * Wahlgruppe („oder“, Migration 384): Positionen derselben Gruppe sind Alternativen. Seit
   * Migration 755 rechnet die Engine einen Wahlgang MENGENGENAU (siehe `wahlGaesteAus`).
   */
  choiceGroupId?: string | null;
  /**
   * Gang-Bezeichnung (Migration 384). Seit der zweiten Pruefung zu sql/755 Teil der EINEN Wahlgang-Definition
   * (`wahlgaengeVerzeichnis`): ein Wahlgang liegt innerhalb EINES Gangs. Aufrufer, die Wahlgaenge mengengenau
   * rechnen, MUESSEN das Feld und die Angebotsreihenfolge (sort_order) mitgeben.
   */
  courseLabel?: string | null;
  /** Tagesbezug fuer per_person; null/undefined => Gaestezahl der Option. */
  dayNo?: number | null;
  /**
   * Migration 725 (Antoine 15.10.2026): „gilt fuer X von Y Gaesten" — per_person rechnet mit dieser
   * Gaestezahl statt der des Tages/der Option. null/undefined = alle Gaeste. `perPersonCents` bleibt
   * Summe / `optionGuestCount` (Durchschnitt je Gast).
   */
  guestCount?: number | null;
  /** Paket, aus dem die Zeile stammt (Kopfzeile + Bestandteile tragen dasselbe). Siehe `paketTeile`. */
  packageId?: string | null;
  /** Katalogpreis eines Paket-Bestandteils (dessen `unitPriceCents` ist 0). Nur fuer `paketTeile`. */
  catalogPriceCents?: number | null;
  /**
   * Punkt R (Antoine 13.10.2026): am Paket hinterlegter Getraenkeanteil, nur an der Paket-Kopfzeile.
   * Einheit wie `unitPriceCents` derselben Zeile. null = nicht hinterlegt (Heuristik), 0 = keine Teilung.
   */
  drinksShareCents?: number | null;
  /**
   * sql/771 (Menuewahl im Paket, docs/KONZEPT-PAKET-MENUEWAHL.md): Kopfzeile EINES Menues, zwischen denen der Gast die
   * Gaeste verteilt (`offer_items.menue_wahl_gruppe`). Preis = Aufpreis je Gast (`per_person`, Menge 1000). Die Zeile
   * zaehlt nie in eine Sektions-Pauschale (`inPauschale`), sie kommt wie „zzgl.“ hinzu. Siehe `menuewahlVerzeichnis`.
   * null/undefined = keine Menuewahl, Bestandsverhalten bitgleich.
   */
  menueWahlGruppe?: string | null;
  /** sql/771: Herkunft (`package_menus.id`) - Kopfzeile UND Bestandteile desselben Menues tragen denselben Wert. */
  paketMenueId?: string | null;
  /** sql/771: Art des Menues (vegan, vegetarisch, fisch_fleisch) - nur Anzeige, kein Einfluss auf Betraege. */
  menueArt?: string | null;
}

export interface EngineInput {
  items: EngineItem[];
  /** Kopf-Gaestezahl der Option (Fallback fuer per_person ohne Tagesbezug). */
  optionGuestCount: number;
  /** dayNo -> Gaestezahl (aus offer_option_days). */
  guestCountByDay?: Record<number, number>;
  /**
   * WARTESCHLANGE PUNKT 32 — die Zubuchungen, die der GAST angehakt hat, mit seiner Menge.
   *
   * Antoine am 16.09.2026: „auf Wunsch zubuchbar muss der Gast optional dazu buchen koennen.
   * Wenn er es auswaehlt erscheint es auf der Rechnung, wenn nicht, dann nicht."
   *
   * Fehlt oder leer = **heutiges Verhalten, bitgleich**: jede optionale Position ist reine
   * Anzeige. Erst ein Eintrag hier macht sie zu einem Betrag.
   *
   * Bei `priceMode: "per_person"` ist der Wert immer 1 — dort steckt die Menge bereits im
   * Zeilenpreis (die Gaestezahl). Antoines Entscheidung dazu, woertlich: „Er darf auch Mengen
   * waehlen", mit genau dieser Ausnahme. AUSNAHME davon (Fix 27.10.2026, V-1262): eine
   * `per_person`-Zubuchung, die das Hauptmenue ERSETZT, ist ein Gaeste-Zaehler - der Wert ist die
   * Zahl der Gaeste k (Betrag = Zeile fuer k Gaeste, das Hauptmenue sinkt um k).
   */
  gebuchteZusatzMengen?: ReadonlyMap<string, number>;
  /**
   * Migration 751: so viele Gaeste essen NICHT das Hauptmenue, weil sie eine ersetzende Zubuchung
   * gewaehlt haben. Das Hauptmenue (nicht-optionale, nicht-„zzgl.“ per_person-Zeilen der Sektion
   * `menu` und die Menue-Pauschale `total`) rechnet dann mit `Gaeste - n`, nie unter 0. Fehlt der
   * Wert, leitet `computeBreakdown` ihn aus `gebuchteZusatzMengen` + `addonErsetztHauptmenue` ab;
   * Aufrufer, die gebuchte Zubuchungen vorab in normale Zeilen umschreiben (Rechnung), geben ihn
   * ausdruecklich mit (`ersetzteGaesteAus`). 0/undefined = Bestandsverhalten, bitgleich.
   */
  ersetzteHauptmenueGaeste?: number | null;
  /**
   * Migration 755 (Antoine 27.10.2026): die Gaeste je Alternative eines Wahlgangs, `{ itemId: anzahl }`
   * (die Auswahl des Gastes, `offer_options.chosen_alternatives` ohne die Gruppen-Ebene). Fehlt sie,
   * traegt die GUENSTIGSTE Alternative eines Wahlgangs alle Gaeste (Grundbetrag der Fassung).
   * Siehe `wahlGaesteAus`.
   */
  wahlMengen?: ReadonlyMap<string, number> | Readonly<Record<string, number>>;
  /**
   * Migration 755, Rueckwaertsvertraeglichkeit: NUR wenn gesetzt (oder wenn `wahlMengen` eine Auswahl
   * nennt) rechnet die Engine Wahlgaenge mengengenau. Fehlt es, gilt das alte Verhalten: jede Alternative
   * mal alle Gaeste mal Menge. Gesetzt wird es aus Daten: `offer_options.wahl_mengengenau` (Option wurde
   * mit der neuen Logik angelegt) bzw. `wahl` im Versand-Snapshot der Fassung.
   */
  wahlMengengenau?: boolean;
  /** Schon aufgeloeste Gaeste je Alternative (`wahlGaesteAus`); gesetzt hat Vorrang vor `wahlMengen`. */
  wahlGaeste?: ReadonlyMap<string, number>;
  /**
   * Fix 27.10.2026 (B1-Nachtrag): je Mitglied eines Wahlgangs das GRUPPEN-Flag „gehoert zum Hauptmenue“
   * (`wahlgangHauptmenueKarte`). Ein Mitglied rechnet mit dem Flag seiner Gruppe, nicht mit dem eigenen
   * (`istHauptmenueZeile`) - sonst zoege eine ersetzende Zubuchung bei gemischten Saetzen (7 %/19 %) nur von
   * einem Teil der Alternativen ab, waehrend Verzeichnis und Annahme (SQL) die ganze Gruppe senken.
   * Fehlt die Karte, leitet `computeBreakdown` sie aus `items` ab.
   */
  wahlHauptmenue?: ReadonlyMap<string, boolean>;
  /** Rabatt in Basispunkten (2500 = 25 %). XOR zu discountAmountCents. */
  discountPercentBp?: number | null;
  /** Rabatt als fester Betrag in Cent. XOR zu discountPercentBp. */
  discountAmountCents?: number | null;
  /**
   * KONZEPT-GESAMTPREIS-PRO-MENUE (20.08.2026), verallgemeinert auf beliebige Sektionen
   * (Bughunt 27.08.2026, Antoine: Getraenkepauschale soll Einzelpositionen wie das Menue
   * kollabieren koennen): je Sektion (Schluessel = `EngineItem.section`, z. B. "menu"/"drinks")
   * ersetzt 'total' die Summe der NICHT-optionalen Zeilen dieser Sektion durch EINEN fixen
   * Betrag - unabhaengig von den einzelnen unitPriceCents der Gang-/Positions-Zeilen. Eine
   * fehlende oder auf 'per_course' stehende Sektion verhaelt sich wie bisher (Summe der
   * Einzelpreise) - keine Verhaltensaenderung an bestehenden Aufrufern, die nur `menu` setzen.
   */
  sectionPricing?: Record<string, { mode: SektionsPreisModus; totalPricePerPersonCents: number | null }>;
}

/**
 * Wie eine Sektion bepreist ist.
 *
 *   `per_course`   Jede Zeile traegt ihren eigenen Preis (Bestand).
 *   `total`        EINE Pauschale JE GAST, mal Gaestezahl (Bestand, KONZEPT-GESAMTPREIS-PRO-MENUE).
 *   `total_event`  EINE Pauschale FUER DIE GANZE VERANSTALTUNG, unabhaengig von der Gaestezahl.
 *
 * `total_event` kam am 17.09.2026 dazu. Der Betreiber wollte eine Raummiete mit Mindestumsatz
 * ueber 6.500 EUR eintragen und bekam 78.000 EUR, weil das Feld „pro Person" bedeutet. Der
 * naheliegende Ausweg (6.500 ÷ 12 = 541,67 EUR je Gast) geht nicht auf — 541,67 × 12 = 6.500,04
 * EUR — und im Angebot stuende ein Personenpreis, wo eine Raumpauschale hingehoert.
 *
 * WARUM EIN DRITTER MODUS und kein zusaetzliches Feld neben dem Betrag: der Modus sagt bereits,
 * wie gerechnet wird. Ein Betrag plus ein Schalter „ist pro Person" waeren zwei Angaben, die
 * einander widersprechen koennen; bestehende Zeilen muessten den Schalter nachtraeglich
 * bekommen. So bleibt `total` unveraendert das, was es immer war — kein Bestandsangebot aendert
 * seine Summe.
 *
 * Das DB-Feld heisst weiterhin `total_price_per_person_cents`. Bei `total_event` traegt es den
 * Gesamtbetrag; die Umdeutung passiert an genau einer Stelle (`alsSektionsModus`, direkt
 * darunter), damit der Name nicht durch den ganzen Baum missverstanden werden kann.
 */
export type SektionsPreisModus = "per_course" | "total" | "total_event";

/**
 * DIE EINE STELLE, an der ein roher `pricing_mode`-Wert zum Engine-Modus wird.
 *
 * Sie steht in der Engine und nicht in der API-Schicht, weil sie ohne Datenbank auskommen muss:
 * das Kundendokument (`apps/api/src/lib/offer-document.ts`) und der Rechnungsbau
 * (`lib/lexoffice.ts`) deuten denselben Wert um, duerfen dafuer aber kein Drizzle-Schema
 * hereinziehen. Genau daran ist es beim Bau von `total_event` beinahe schiefgegangen: beide
 * Schichten trugen ihre eigene Ternary `mode === "total" ? "total" : "per_course"`, und die
 * stufte den neuen dritten Modus still auf `per_course` zurueck. Das Kundendokument haette dann
 * „gesamt" beschriftet und Einzelpreise gerechnet — ein Beleg, der sich selbst widerspricht.
 *
 * Ein unbekannter Wert faellt bewusst auf `per_course` zurueck: der harmloseste der drei, weil er
 * nichts kollabiert und nichts pauschal setzt. Alt-Snapshots vor dem 27.08.2026 kennen das Feld
 * gar nicht und landen genau dort.
 */
export function alsSektionsModus(roh: string | null | undefined): SektionsPreisModus {
  return roh === "total" || roh === "total_event" ? roh : "per_course";
}

/**
 * Traegt die Sektion EINEN Betrag statt Einzelpreisen? Gilt fuer beide Pauschal-Modi.
 *
 * Diese Frage stellen drei Schichten unabhaengig voneinander — die Engine (welche Zeilen
 * kollabieren), das Kundendokument (welche Zeile einen Einzelpreis ausweist) und der
 * Rechnungsbau (welche Zeilen an LexOffice gehen). Stimmen sie nicht ueberein, weicht die
 * Rechnung vom unterschriebenen Angebot ab. Genau das war der Bughunt-Befund vom 16.09.2026
 * (RE0086 lag 127,56 EUR zu hoch), und ein dritter Modus verdreifacht die Gelegenheit dazu —
 * deshalb beantwortet die Frage ab hier eine Funktion statt dreier gleichlautender Ternaries.
 */
export function istPauschal(modus: string | null | undefined): boolean {
  return modus === "total" || modus === "total_event";
}

export interface VatBucket {
  rateBp: number;
  netCents: number;
  vatCents: number;
  grossCents: number;
}

export interface LineTotal {
  id?: string;
  grossCents: number;
}

export interface PriceBreakdown {
  subtotalCents: number; // Summe der Nicht-Anpassungs-Zeilen (brutto)
  discountCents: number; // effektiv gewaehrter Rabatt (>= 0)
  /** Migration 755: die rabattfaehige Basis (Brutto), auf die der Rabatt gerechnet wurde. */
  discountBaseCents: number;
  adjustmentCents: number; // Summe der Anpassungs-Zeilen (kann negativ sein)
  amountTotalCents: number; // subtotal - discount + adjustment
  perPersonCents: number; // amountTotal / optionGuestCount (0 bei 0 Gaesten)
  vatBreakdown: VatBucket[]; // je Satz, aufsteigend nach rateBp
  lineTotals: LineTotal[];
  /**
   * Bruttosumme je Abschnitt — fuer Zwischenueberschriften im Builder, im Angebotsdokument und
   * in jeder anderen Anzeige, die einen Abschnitt beziffert.
   *
   * SIE GEHOERT IN DIE ENGINE, weil sie dieselben Regeln braucht wie `subtotalCents`: optionale
   * Zeilen zaehlen nicht mit, eine Sektion im Gesamtpreis-Modus kollabiert ihre Zeilen zugunsten
   * EINES Betrags. Wer `lineTotals` selbst je Abschnitt aufsummiert, muss diese Regeln kennen und
   * wiederholen — genau daran ist der Builder am 17.09.2026 gescheitert (Fall Kokorov: eine
   * optionale Position ueber 250,00 EUR stand in der Abschnittssumme, aber nicht in der
   * Gesamtsumme; der Betreiber sah zwei Preise nebeneinander, die nicht zusammenpassten).
   *
   * Antoine dazu, woertlich: "Das muss ueberall funktionieren und darf ueberall nur eine
   * Recheneinheit sein, die richtige."
   *
   * `adjustment` ist NICHT enthalten — die Zielpreis-Korrektur ist kein Leistungsposten.
   * Abschnitte ohne Betrag fehlen; die Reihenfolge ist die des ersten Auftretens in `items`,
   * danach die der Gesamtpreis-Sektionen.
   */
  sectionTotals: SectionTotal[];
}

/** Bruttosumme eines Abschnitts. Siehe `PriceBreakdown.sectionTotals`. */
export interface SectionTotal {
  section: string;
  grossCents: number;
}

/**
 * Migration 751: gehoert die Zeile zum HAUPTMENUE, dessen Gaestezahl eine ersetzende Zubuchung senkt?
 * Verbindliche (weder „zubuchbar“ noch „zzgl.“) per_person-Zeilen der Sektion `menu` - AUSSER Zeilen
 * mit 19 % USt (`GETRAENKE_SATZ_BP`).
 *
 * Warum der Satz (Fix 27.10.2026, Geld-Fehler seit #1846): „vegetarisch statt Hauptmenue“ ersetzt das
 * ESSEN, nicht die Getraenke. Ohne die Ausnahme zog eine ersetzende Zubuchung ihre Gaeste auch von einer
 * Getraenkepauschale in der Menue-Sektion ab (25 Gaeste, Paket 82 EUR, Getraenkepauschale 30 EUR, 3 x
 * vegetarisch 72 EUR: 2.680 EUR statt richtig 2.770 EUR). offer_items hat keine Kategorie-Spalte; Getraenke
 * erkennt die Engine - wie in `pauschalTeile` - am Satz 19 %. Bei 0-%-/§19-Mandanten bleibt alles wie zuvor.
 */
export function istHauptmenueZeile(item: HauptmenueMerkmale): boolean {
  return item.section === "menu" && item.priceMode === "per_person" && inPauschale(item) && item.vatRateBp !== GETRAENKE_SATZ_BP;
}

/** Was `istHauptmenueZeile` von einer Zeile braucht (Engine-Zeile oder Verzeichnis-Position). */
export interface HauptmenueMerkmale {
  section: string;
  priceMode: string;
  isOptional?: boolean | null;
  surcharge?: boolean | null;
  vatRateBp?: number | null;
}

/**
 * DIE EINE gruppenweite Entscheidung (Fix 27.10.2026, B1-Nachtrag): gehoert dieser WAHLGANG zum Hauptmenue, dessen
 * Gaeste eine ersetzende Zubuchung („vegetarisch statt Hauptmenue“) senkt? Ja, sobald MINDESTENS EINE Alternative
 * eine Hauptmenue-Zeile ist (`istHauptmenueZeile`: Speise, also nicht 19 %). Nur ein reiner Getraenke-Wahlgang
 * (alle Alternativen 19 %, z. B. Wein rot/weiss) behaelt seine Gaeste.
 *
 * Warum gruppenweit: der Gast verteilt die Gaeste EINES Wahlgangs auf die Alternativen; die Soll-Summe (Annahme,
 * SQL) gilt fuer die Gruppe. Entschiede die Engine je Zeile (Dessert A 7 % senkt, Dessert B 19 % nicht), rechnete
 * die Vorschau mit 22 Gaesten, die Annahme verlangte 25 - die Vorschau waere nie annehmbar. Engine (`guestsFor`,
 * `wahlGaesteAus`), Preisliste (`wahlTabelle`), Verzeichnis (`wahlgaengeSnapshot`) und Gastseite nutzen DIESE Funktion.
 */
export function wahlgangIstHauptmenue(mitglieder: readonly HauptmenueMerkmale[]): boolean {
  return mitglieder.some((m) => istHauptmenueZeile(m));
}

/** Je Mitglied eines Wahlgangs (`wahlgaengeVerzeichnis`) das Gruppen-Flag `wahlgangIstHauptmenue`. */
export function wahlgangHauptmenueKarte(positionen: readonly (WahlgangPosition & HauptmenueMerkmale)[]): Map<string, boolean> {
  const out = new Map<string, boolean>();
  const nachId = new Map<string, WahlgangPosition & HauptmenueMerkmale>();
  for (const p of positionen) if (p.id) nachId.set(p.id, p);
  for (const w of wahlgaengeVerzeichnis(positionen)) {
    const flag = wahlgangIstHauptmenue(w.ids.map((id) => nachId.get(id)!));
    for (const id of w.ids) out.set(id, flag);
  }
  return out;
}

/** Hauptmenue-Zeile im Angebot: ein Wahlgang-Mitglied traegt das Flag seiner Gruppe (`wahlHauptmenue`), sonst die Zeile selbst. */
function zaehltAlsHauptmenue(item: EngineItem, input: Pick<EngineInput, "wahlHauptmenue">): boolean {
  const g = item.id ? input.wahlHauptmenue?.get(item.id) : undefined;
  return g ?? istHauptmenueZeile(item);
}

/** Wie viele Gaeste das Hauptmenue NICHT essen (>= 0, ganzzahlig). */
function ersetzteGaeste(input: Pick<EngineInput, "ersetzteHauptmenueGaeste">): number {
  const n = input.ersetzteHauptmenueGaeste;
  return n != null && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Gaestezahl fuer eine Zeile: Teilgruppe (Migration 725) vor Tagesbezug vor Options-Default, abzueglich ersetzter Hauptmenue-Gaeste (Migration 751). */
export function guestsFor(item: EngineItem, input: Pick<EngineInput, "optionGuestCount" | "guestCountByDay" | "ersetzteHauptmenueGaeste" | "wahlGaeste" | "wahlHauptmenue">): number {
  // Migration 755: Mitglied eines mengengenau gerechneten Wahlgangs - die Gaeste stehen fest.
  if (item.id && input.wahlGaeste) {
    const w = input.wahlGaeste.get(item.id);
    if (w !== undefined) return w;
  }
  return guestsForOhneWahl(item, input);
}

export function guestsForOhneWahl(item: EngineItem, input: Pick<EngineInput, "optionGuestCount" | "guestCountByDay" | "ersetzteHauptmenueGaeste" | "wahlHauptmenue">): number {
  const alle = item.dayNo != null && input.guestCountByDay && item.dayNo in input.guestCountByDay
    ? input.guestCountByDay[item.dayNo]!
    : input.optionGuestCount;
  // Gedeckelt: sinkt die Gaestezahl unter die Teilgruppe, rechnet die Zeile nie mehr Gaeste als da sind.
  const g = item.guestCount != null && item.guestCount > 0 ? Math.min(item.guestCount, alle) : alle;
  const k = ersetzteGaeste(input);
  return k > 0 && zaehltAlsHauptmenue(item, input) ? Math.max(0, g - k) : g;
}

/**
 * Entscheidung des Inhabers (27.10.2026): die Aufstockung auf einen Mindestumsatz wird IMMER mit 19 % USt
 * gerechnet - fest, unabhaengig vom USt-Satz der Position (Raum-/Mindestumsatz ist Gastronomie vor Ort mit
 * Raumnutzung, kein Speisenumsatz zu 7 %). Eine Stelle fuer Rechnung, Beleg, Dokument, Anzahlung und Vorschau.
 */
export const AUFSTOCKUNG_MINDESTUMSATZ_VAT_BP = 1900;

/** Eine Position, wie das Wahlgang-Verzeichnis sie liest (Reihenfolge = Reihenfolge des Angebots). */
export interface WahlgangPosition {
  id?: string | null;
  section: string;
  /**
   * Fix 27.10.2026 (B1): Satz und „zzgl.“ - damit das Verzeichnis erkennt, ob der Wahlgang zum HAUPTMENUE
   * gehoert (`istHauptmenueZeile`: 19 % = Getraenk, ersetzende Zubuchungen senken seine Gaeste nicht).
   * Fehlt der Satz, gilt die Zeile wie vor dem Fix als Speise.
   */
  vatRateBp?: number | null;
  surcharge?: boolean | null;
  isOptional?: boolean | null;
  courseLabel?: string | null;
  choiceGroupId?: string | null;
  /**
   * sql/771: Kopfzeile einer Menuewahl im Paket. Sie ist NIE Mitglied eines Wahlgangs, auch wenn sie (fehlerhaft) eine
   * `choiceGroupId` traegt - sonst rechneten Wahlgang und Menuewahl dieselbe Zeile doppelt.
   */
  menueWahlGruppe?: string | null;
  /** Nur `per_person`-Positionen koennen Mitglied eines Wahlgangs sein. */
  priceMode: string;
  /** Nur ganze Portionen je Gast (1000) - bei 1/3-Portionen aendert die Zuordnung nichts. */
  qtyMilli: number;
}

/** Ein Wahlgang, wie die Gastseite ihn zeigt: Gang-Bezeichnung und die Positionen (ids) in Angebotsreihenfolge. */
export interface WahlgangVerzeichnisEintrag {
  gruppe: string;
  gang: string | null;
  ids: string[];
}

/**
 * DIE EINE Definition von „Wahlgang“ (Opus-/Fable-Pruefung zu sql/755, zweite Runde): nicht optionale Positionen
 * der Sektionen Menue und Getraenke; innerhalb EINES Gangs (aufeinanderfolgende gleiche Gang-Bezeichnung) bilden
 * aufeinanderfolgende Positionen derselben `choiceGroupId` einen Wahlgang, sobald es mindestens zwei sind UND
 * alle `per_person` mit genau einer Portion je Gast (qtyMilli 1000) sind - nur dann aendert die Gaeste-Zuordnung
 * etwas. Nur das, was die Gastseite so zeigt, ist Wahlgang - und nur darauf bestehen Annahme-Sperre, Mengen und
 * die mengengenaue Rechnung (`wahlGaesteAus`). Engine, Versand-Snapshot (SQL liest ihn) und Gastseite nutzen DIESE
 * Funktion; alles andere (Restgruppen nach Drag&Drop, andere Sektion, gemischte Preismodi) rechnet wie vor sql/755.
 */
export function wahlgaengeVerzeichnis(positionen: readonly WahlgangPosition[]): WahlgangVerzeichnisEintrag[] {
  const speisen = positionen.filter((p) => (p.section === "menu" || p.section === "drinks") && p.isOptional !== true);
  const out: WahlgangVerzeichnisEintrag[] = [];
  const gang: { label: string | null; zeilen: WahlgangPosition[] }[] = [];
  for (const p of speisen) {
    const label = p.courseLabel?.trim() ? p.courseLabel.trim() : null;
    const letzter = gang[gang.length - 1];
    if (letzter && letzter.label === label) letzter.zeilen.push(p);
    else gang.push({ label, zeilen: [p] });
  }
  for (const g of gang) {
    const bloecke: { gruppe: string | null; zeilen: WahlgangPosition[] }[] = [];
    for (const z of g.zeilen) {
      // sql/771: eine Menue-Kopfzeile bildet immer einen eigenen Block ohne Gruppe (nie Wahlgang, unterbricht ihn).
      const gruppe = z.menueWahlGruppe != null ? null : (z.choiceGroupId ?? null);
      const l = bloecke[bloecke.length - 1];
      if (gruppe != null && l && l.gruppe === gruppe) l.zeilen.push(z);
      else bloecke.push({ gruppe, zeilen: [z] });
    }
    for (const b of bloecke) {
      if (
        b.gruppe != null &&
        b.zeilen.length > 1 &&
        b.zeilen.every((z) => z.id && z.priceMode === "per_person" && Number(z.qtyMilli) === 1000)
      ) {
        out.push({ gruppe: b.gruppe, gang: g.label, ids: b.zeilen.map((z) => z.id as string) });
      }
    }
  }
  return out;
}

/** Mehr Gaeste frieren wir nicht ein (Groesse des Snapshots). */
export const WAHL_MAX_GAESTE = 2000;

/** Die groesste Gaestezahl der Option oder eines ihrer Tage. */
export function groessteGaestezahl(input: Pick<EngineInput, "optionGuestCount" | "guestCountByDay">): number {
  let n = input.optionGuestCount;
  for (const g of Object.values(input.guestCountByDay ?? {})) if (Number.isFinite(g) && g > n) n = g;
  return n;
}

/**
 * Migration 755 (Antoine 27.10.2026): WAHLGAENGE („oder“, Gaeste je Alternative) werden MENGENGENAU
 * gerechnet. Betrag eines Wahlgangs = Summe (Gaeste_i x Preis_i) - nicht mehr jede Alternative mit
 * allen Gaesten.
 *
 * Wirkt NUR, wenn ALLES zutrifft (sonst exakt wie vor sql/755: jede Alternative x alle Gaeste x Menge):
 *  - `wahlMengengenau === true` (Option mit der neuen Logik angelegt bzw. `wahl` im Versand-Snapshot) -
 *    eine Auswahl allein schaltet NICHT um;
 *  - die Gruppe steht im Verzeichnis (`wahlgaengeVerzeichnis` - dieselbe Definition wie Gastseite und SQL:
 *    Menue/Getraenke, ein Gang, aufeinanderfolgend, mindestens zwei, alle `per_person` mit Menge 1000);
 *  - kein Mitglied ist Paketbestandteil oder in einer Menue-/Sektions-Pauschale aufgegangen;
 *  - mindestens eine Alternative traegt einen Preis (ohne Preis stecken die Gerichte im Menue-/Paketpreis).
 *
 * Aufloesung je Wahlgang:
 *  - mit Auswahl (`wahlMengen` nennt mindestens ein Mitglied): jede Alternative rechnet mit ihrer
 *    Gaestezahl (nicht genannt = 0), gedeckelt auf die Gaeste der Alternative (wie SQL). Die Auswahl ist
 *    schon netto (Summe = Gaeste mit Hauptmenue), die ersetzten Gaeste werden nicht noch einmal abgezogen.
 *  - ohne Auswahl (Grundbetrag der Fassung, noch nichts gewaehlt): die GUENSTIGSTE Alternative (bei
 *    Gleichstand die erste) traegt alle Gaeste, die uebrigen 0.
 *
 * Rueckgabe: nur die Mitglieder solcher Wahlgaenge. Leer = nichts zu tun.
 */
export function wahlGaesteAus(
  items: readonly EngineItem[],
  input: Pick<EngineInput, "optionGuestCount" | "guestCountByDay" | "ersetzteHauptmenueGaeste" | "sectionPricing" | "wahlMengen" | "wahlMengengenau" | "wahlHauptmenue">,
): Map<string, number> {
  const out = new Map<string, number>();
  // Alte Angebote (ohne Flag) rechnen exakt wie vor Migration 755 - auch wenn eine Auswahl vorliegt.
  if (input.wahlMengengenau !== true) return out;
  // Mehr Gaeste friert der Versand nicht ein (Snapshot-Groesse) - dort rechnet der Wahlgang wie bisher.
  if (input.optionGuestCount < 1 || groessteGaestezahl(input) > WAHL_MAX_GAESTE) return out;
  const nachId = new Map<string, EngineItem>();
  for (const it of items) if (it.id) nachId.set(it.id, it);
  const mengen = input.wahlMengen;
  const hol = (id: string): number | undefined => (!mengen ? undefined : mengen instanceof Map ? mengen.get(id) : (mengen as Record<string, number>)[id]);
  for (const w of wahlgaengeVerzeichnis(items)) {
    const mitglieder = w.ids.map((id) => nachId.get(id)!);
    if (mitglieder.some((m) => m.packageId)) continue;
    // In einer Pauschal-Sektion zaehlt die Zeile nicht fuer sich (Gerichte stecken im Pauschalpreis).
    if (mitglieder.some((m) => istPauschal(input.sectionPricing?.[m.section]?.mode) && inPauschale(m))) continue;
    if (!mitglieder.some((m) => m.unitPriceCents > 0)) continue;
    const gewaehlt = mitglieder.some((m) => hol(m.id!) !== undefined);
    if (gewaehlt) {
      for (const m of mitglieder) out.set(m.id!, Math.min(guestsForOhneWahl(m, { ...input, ersetzteHauptmenueGaeste: 0 }), Math.max(0, Math.floor(hol(m.id!) ?? 0))));
      continue;
    }
    // Guenstigste Alternative = kleinster Preis je Gast, bei Gleichstand die erste. Ersetzte Gaeste zieht sie nach
    // dem GRUPPEN-Flag ab (`wahlgangIstHauptmenue`), nicht nach ihrem eigenen Satz - wie Verzeichnis und Annahme.
    let guenstigste = mitglieder[0]!;
    for (const m of mitglieder) if (m.unitPriceCents < guenstigste.unitPriceCents) guenstigste = m;
    const flag = wahlgangIstHauptmenue(mitglieder);
    const gruppe = new Map(mitglieder.map((m) => [m.id!, flag] as const));
    for (const m of mitglieder) out.set(m.id!, m === guenstigste ? guestsForOhneWahl(m, { ...input, wahlHauptmenue: gruppe }) : 0);
  }
  return out;
}

/**
 * Migration 751 — DIE EINE Regel, wie viele Gaeste ersetzende Zubuchungen dem Hauptmenue wegnehmen.
 * Die gebuchte Menge = Zahl der Gaeste (seit 27.10.2026 auch bei `per_person`: Gaeste-Zaehler 0..Gaeste der
 * Zeile, V-1262). Gedeckelt auf die Gaestezahl der Option. `mengen`: `{ itemId: menge }`.
 */
export function ersetzteGaesteAus(
  items: readonly Pick<EngineItem, "id" | "priceMode" | "isOptional" | "addonErsetztHauptmenue" | "guestCount" | "dayNo">[],
  mengen: ReadonlyMap<string, number> | Readonly<Record<string, number>> | undefined,
  optionGuestCount: number,
): number {
  if (!mengen) return 0;
  const hol = (id: string): number | undefined => (mengen instanceof Map ? mengen.get(id) : (mengen as Record<string, number>)[id]);
  let n = 0;
  for (const it of items) {
    if (it.isOptional !== true || it.addonErsetztHauptmenue !== true || !it.id) continue;
    const m = hol(it.id);
    if (!m || m <= 0) continue;
    // Fix 27.10.2026 (V-1262): auch `per_person` ist ein GAESTE-ZAEHLER - die Menge ist die Zahl der Gaeste, die
    // statt des Hauptmenues diese Zubuchung essen (gedeckelt auf die Teilgruppe). Alt-Annahmen (qty 1 = alle) kommen
    // ueber `gebuchteMengenAus` mit ihrem `ersetzt` (= alle Gaeste der Zeile) hier an und rechnen unveraendert.
    n += it.priceMode === "per_person"
      ? Math.min(Math.floor(m), it.guestCount != null && it.guestCount > 0 ? Math.min(it.guestCount, optionGuestCount) : optionGuestCount)
      : m;
  }
  return Math.min(Math.max(0, optionGuestCount), n);
}

/** sql/771: die drei Arten eines Menues (CHECK an `package_menus.art` und `offer_items.menue_art`). */
export const MENUE_ARTEN = ["vegan", "vegetarisch", "fisch_fleisch"] as const;
export type MenueArt = (typeof MENUE_ARTEN)[number];

/** Eine Menuewahl, wie Versand-Snapshot (`menuewahlen`), Annahme (SQL) und Gastseite sie lesen. */
export interface MenuewahlEintrag {
  /** `offer_items.menue_wahl_gruppe`. */
  gruppe: string;
  /** Die Menue-Kopfzeilen in Angebotsreihenfolge. */
  ids: string[];
  /**
   * So viele Gaeste verteilt der Gast - die Gaeste der Paketzeile (Tag/Teilgruppe). Ersetzte Hauptmenue-Gaeste werden
   * NICHT abgezogen (`ersetzteHauptmenueGaeste: 0`): eine Menuewahl, auf deren Paketzeile eine ersetzende Zubuchung
   * wirkt, ist deshalb ungueltig (W1, `menuewahlVerzeichnis`) - sonst zahlten Gaeste, die das Paketmenue gar nicht
   * essen, dessen Aufpreis.
   */
  soll: number;
  /** Das Menue, das ohne Wahl alle Gaeste traegt: kleinster Aufpreis, bei Gleichstand das erste. */
  guenstigsteId: string;
  /** Aufpreis je Gast in Cent je Kopfzeile. */
  aufpreise: Record<string, number>;
  /** Art je Kopfzeile (nur Anzeige). */
  arten: Record<string, string | null>;
  /** Herkunft je Kopfzeile (`package_menus.id`). */
  paketMenueIds: Record<string, string | null>;
  dayNo: number | null;
}

/**
 * sql/771 (Antoine 01.11.2026, docs/KONZEPT-PAKET-MENUEWAHL.md): DIE EINE Definition einer Menuewahl im Paket.
 *
 * Kopfzeilen (`menueWahlGruppe` gesetzt, nicht optional) derselben Gruppe bilden eine Menuewahl, sobald es MINDESTENS
 * ZWEI sind, alle `per_person` mit genau einem Menue je Gast (Menge 1000), alle am selben Tag. Ein einzelnes Menue ist
 * keine Wahl: seine Kopfzeile rechnet wie jede Zeile mit allen Gaesten (keine Zaehler, keine Sperre).
 * Gruppen, die diese Bedingungen nicht erfuellen, sind KEINE Menuewahl (dann rechnet jede Kopfzeile wie eine normale
 * Zeile) - der Paket-Editor/apply-package (Einheit 3) legt nur gueltige Gruppen an.
 *
 * Ebenfalls KEINE Menuewahl (Geld-Zweitpruefung zu 416b6cf6):
 *  - S1: ein Bestandteil eines ihrer Menues (gleiche `paketMenueId`, gleiches Paket, gleicher Tag, keine Kopfzeile, nicht
 *    optional) traegt einen eigenen Preis. Der Betrag einer Menuewahl ist Grundpreis + Summe (Gaeste_i x Aufpreis_i); ein
 *    bepreister Bestandteil rechnete mit den Gaesten seines Menues, die eingefrorene Preisliste (`wahlTabelle`, s[k]) aber
 *    mit denen des guenstigsten - Annahme (SQL) und Engine liefen auseinander. Der Preis gehoert in den Aufpreis der Kopfzeile.
 *  - W1: eine ersetzende Zubuchung („vegetarisch statt Hauptmenue“) wirkt auf das Paket - sie gehoert zu demselben Paket,
 *    oder die Paketzeile bzw. ein Bestandteil ist eine Hauptmenue-Zeile (`istHauptmenueZeile`). `soll` zaehlt die ersetzten
 *    Gaeste mit; Menuewahl und ersetzende Zubuchung im selben Paket sind unzulaessig (Plan; Einheit 3 lehnt sie im
 *    Builder mit 422 ab).
 *
 * `soll` = Gaeste der Paketzeile (gleiches `packageId`, keine Menue-Herkunft, gleicher Tag), sonst der ersten Kopfzeile.
 */
export function menuewahlVerzeichnis(
  items: readonly EngineItem[],
  gaeste: Pick<EngineInput, "optionGuestCount" | "guestCountByDay">,
): MenuewahlEintrag[] {
  const gruppen = new Map<string, EngineItem[]>();
  for (const it of items) {
    if (it.menueWahlGruppe == null || it.isOptional === true || it.section === ADJUSTMENT_SECTION) continue;
    const l = gruppen.get(it.menueWahlGruppe);
    if (l) l.push(it);
    else gruppen.set(it.menueWahlGruppe, [it]);
  }
  const out: MenuewahlEintrag[] = [];
  for (const [gruppe, koepfe] of gruppen) {
    if (koepfe.length < 2) continue;
    if (!koepfe.every((k) => k.id && k.priceMode === "per_person" && Number(k.qtyMilli) === 1000 && Number.isSafeInteger(k.unitPriceCents) && k.unitPriceCents >= 0)) continue;
    const tag = koepfe[0]!.dayNo ?? null;
    if (!koepfe.every((k) => (k.dayNo ?? null) === tag)) continue;
    const pid = koepfe[0]!.packageId ?? null;
    const paketZeile = pid
      ? items.find((it) => it.packageId === pid && it.menueWahlGruppe == null && it.paketMenueId == null && it.isOptional !== true && it.section !== ADJUSTMENT_SECTION && (it.dayNo ?? null) === tag)
      : undefined;
    // S1: Bestandteile (dieselbe Zuordnung wie `menuewahlGaesteAus`) duerfen keinen eigenen Preis tragen.
    const herkunft = new Set(koepfe.map((k) => k.paketMenueId).filter((m): m is string => m != null));
    const bestandteile = items.filter((it) =>
      it.menueWahlGruppe == null && it.paketMenueId != null && herkunft.has(it.paketMenueId) && it.isOptional !== true
      && (it.packageId ?? null) === pid && (it.dayNo ?? null) === tag);
    if (bestandteile.some((it) => Number(it.unitPriceCents) !== 0 && Number(it.qtyMilli) !== 0)) continue;
    // W1: keine ersetzende Zubuchung, die auf dieses Paket wirkt.
    const ersetzende = items.filter((it) => it.isOptional === true && it.addonErsetztHauptmenue === true);
    if (ersetzende.length > 0) {
      const wirkt = ersetzende.some((z) => pid != null && (z.packageId ?? null) === pid)
        || (paketZeile != null && istHauptmenueZeile(paketZeile))
        || bestandteile.some((it) => istHauptmenueZeile(it));
      if (wirkt) continue;
    }
    const soll = guestsForOhneWahl(paketZeile ?? koepfe[0]!, { optionGuestCount: gaeste.optionGuestCount, guestCountByDay: gaeste.guestCountByDay, ersetzteHauptmenueGaeste: 0 });
    let guenstigste = koepfe[0]!;
    for (const k of koepfe) if (k.unitPriceCents < guenstigste.unitPriceCents) guenstigste = k;
    out.push({
      gruppe,
      ids: koepfe.map((k) => k.id as string),
      soll: Math.max(0, Math.floor(soll)),
      guenstigsteId: guenstigste.id as string,
      aufpreise: Object.fromEntries(koepfe.map((k) => [k.id as string, k.unitPriceCents])),
      arten: Object.fromEntries(koepfe.map((k) => [k.id as string, k.menueArt ?? null])),
      paketMenueIds: Object.fromEntries(koepfe.map((k) => [k.id as string, k.paketMenueId ?? null])),
      dayNo: tag,
    });
  }
  return out;
}

type Mengen = ReadonlyMap<string, number> | Readonly<Record<string, number>>;
const holMenge = (mengen: Mengen | undefined, id: string): number | undefined =>
  !mengen ? undefined : mengen instanceof Map ? mengen.get(id) : (mengen as Record<string, number>)[id];

/**
 * sql/771: DIE EINE Aufloesung einer Menuewahl (Engine, `menuewahlBetrag`, SQL `wahl_mengen_bereinigt`):
 * die Auswahl gilt NUR, wenn sie vollstaendig ist - jede Menge eine ganze Zahl 0..soll (nicht genannt = 0), Summe
 * genau `soll`, mindestens ein Menue genannt. Sonst (nichts oder nur teilweise gewaehlt) traegt das guenstigste Menue
 * alle Gaeste. So kann eine halbe Auswahl nie einen kleineren Betrag ergeben als der Grundbetrag der Fassung.
 */
export function menuewahlAufloesen(eintrag: MenuewahlEintrag, mengen: Mengen | undefined): { gewaehlt: boolean; gaeste: Map<string, number> } {
  const roh = eintrag.ids.map((id) => holMenge(mengen, id));
  const gueltig = roh.some((m) => m !== undefined)
    && roh.every((m) => m === undefined || (Number.isInteger(m) && m >= 0 && m <= eintrag.soll))
    && roh.reduce<number>((s, m) => s + (m ?? 0), 0) === eintrag.soll;
  const gaeste = new Map<string, number>();
  eintrag.ids.forEach((id, i) => gaeste.set(id, gueltig ? (roh[i] ?? 0) : id === eintrag.guenstigsteId ? eintrag.soll : 0));
  return { gewaehlt: gueltig, gaeste };
}

/**
 * sql/771: die Gaeste je Menue-Kopfzeile UND je Bestandteil eines Menues (gleiche `paketMenueId`, gleiches Paket, gleicher
 * Tag) - fuer `computeBreakdown` (`wahlGaeste`). Die Auswahl kommt aus `wahlMengen` (`{ kopfzeile: anzahl }`, dasselbe
 * flache Format wie Wahlgaenge). Ohne Menuewahl leer.
 */
export function menuewahlGaesteAus(
  items: readonly EngineItem[],
  input: Pick<EngineInput, "optionGuestCount" | "guestCountByDay" | "wahlMengen">,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const v of menuewahlVerzeichnis(items, input)) {
    const { gaeste } = menuewahlAufloesen(v, input.wahlMengen);
    for (const [id, n] of gaeste) out.set(id, n);
    const kopf = new Map(items.filter((it) => it.id && gaeste.has(it.id)).map((it) => [it.paketMenueId ?? null, it] as const));
    for (const it of items) {
      if (!it.id || it.menueWahlGruppe != null || it.paketMenueId == null || it.isOptional === true) continue;
      const k = kopf.get(it.paketMenueId);
      if (!k || (k.packageId ?? null) !== (it.packageId ?? null) || (k.dayNo ?? null) !== (it.dayNo ?? null)) continue;
      out.set(it.id, gaeste.get(k.id!) ?? 0);
    }
  }
  return out;
}

/**
 * G13 (BUGHUNT-2026-08-15): Integer-/Groessen-Guard der Engine. `qtyMilli * unitPriceCents *
 * guests` kann 2^53 ueberschreiten — ab dort rechnet IEEE-754 still falsch, und die Engine ist
 * die einzige Wahrheit fuer Betraege. Ein Wert ausserhalb des sicheren Bereichs (oder NaN/
 * Infinity/Bruchzahl, wo eine Ganzzahl vereinbart ist) ist IMMER ein Aufrufer-Fehler und wird
 * geworfen statt still verrechnet.
 */
function sichereGanzzahl(wert: number, kontext: string): number {
  if (!Number.isSafeInteger(wert)) {
    throw new RangeError(`Preis-Engine: ${kontext} verlaesst den sicheren Ganzzahlbereich (${wert}).`);
  }
  return wert;
}

/** Mehr Gaeste frieren wir je Zaehler nicht ein (Groesse des Snapshots) — darueber bleibt die Zubuchung ein Schalter. */
export const GAESTE_ZAEHLER_MAX = 2000;

/**
 * sql/763 (Antoine, 28.10.2026): die Betragsreihe eines Gaeste-Zaehlers einer `per_person`-Zubuchung —
 * `[k]` = Brutto der Zeile fuer k Gaeste, k = 0..max (max = Gaeste der Zeile: Teilgruppe gedeckelt auf
 * `alleGaeste`). Der Versand friert sie ein, Annahme (SQL) und Gastseite schlagen nur nach; dieselbe
 * Rechnung wie die Rechnung (Zeile mit Teilgruppe k, `zubuchungenEinrechnen`). `null`, wenn es nichts zu zaehlen gibt (kein
 * `per_person`, keine Gaeste, mehr als `GAESTE_ZAEHLER_MAX`).
 */
export function gaesteZaehlerReihe(item: EngineItem, alleGaeste: number): number[] | null {
  if (item.priceMode !== "per_person") return null;
  const g = Math.max(0, Math.floor(alleGaeste));
  const max = item.guestCount != null && item.guestCount > 0 ? Math.min(item.guestCount, g) : g;
  if (max < 1 || max > GAESTE_ZAEHLER_MAX) return null;
  const reihe: number[] = [];
  for (let k = 0; k <= max; k++) reihe.push(lineGross(item, k));
  return reihe;
}

/** Brutto-Zeilensumme in Cent. Division durch 1000 erst nach der Multiplikation, dann runden. */
export function lineGross(item: EngineItem, alleGaeste: number): number {
  // Migration 725: eine Teilgruppe gilt in JEDEM Aufrufer (Builder-Summen, Abschnitte), nicht nur
  // in computeBreakdown — idempotent, wenn der Aufrufer schon guestsFor() uebergibt.
  const guests = item.guestCount != null && item.guestCount > 0 ? Math.min(item.guestCount, alleGaeste) : alleGaeste;
  switch (item.priceMode) {
    case "per_person":
      // G13: das VOLLE Produkt pruefen, nicht erst das gerundete Ergebnis — die Praezision geht
      // in der Multiplikation verloren, nicht in der Division.
      return Math.round(sichereGanzzahl(item.qtyMilli * item.unitPriceCents * guests, "Zeilenprodukt (per_person)") / 1000);
    case "per_unit":
      return Math.round(sichereGanzzahl(item.qtyMilli * item.unitPriceCents, "Zeilenprodukt (per_unit)") / 1000);
    case "flat":
      // G13: vorher ungeprueft UND ungerundet — ein Bruch-Cent-Wert waere unveraendert in die
      // Buckets gelaufen und haette die Cent-Exaktheit aller Folgerechnungen gebrochen.
      return sichereGanzzahl(Math.round(item.unitPriceCents), "Einzelpreis (flat)");
    default:
      /**
       * EIN UNBEKANNTER PREISMODUS DARF KEINE ZAHL ERFINDEN — und erst recht keine, die gar
       * keine ist (21.09.2026).
       *
       * Bis hierher hatte das `switch` keinen Zweig fuer den Rest. Der Typ `EngineItem` verspricht
       * zwar nur die drei Modi, aber `priceMode` kommt als ROHER TEXT aus der Datenbank, aus
       * Versand-Snapshots (jsonb), aus KI-Vorschlaegen und aus Paketen — und ein `switch` ohne
       * `default` liefert dort `undefined`. Das rechnete sich durch die ganze Engine zu `NaN`
       * weiter und stand am Ende als **„Zwischensumme NaN €"** im Dokument, das an den Kunden
       * geht. Aufgefallen beim ersten Angebots-PDF; `offer_items` traegt zwar einen CHECK auf die
       * drei Modi (in Produktion gibt es nur sie), die anderen Quellen aber nicht.
       *
       * Ein geworfener Fehler haelt den Versand auf und nennt die Ursache. Eine stille NaN-Zahl
       * haelt nichts auf und behauptet einen Betrag, den es nicht gibt — das ist der schlechtere
       * Ausgang.
       */
      throw new RangeError(`Preis-Engine: unbekannter Preismodus "${String((item as { priceMode: unknown }).priceMode)}".`);
  }
}

/** USt aus Brutto fuer einen Satz (bp). rate 0 => 0. */
export function vatFromGross(grossCents: number, rateBp: number): number {
  if (rateBp <= 0) return 0;
  const net = Math.round((grossCents * 10000) / (10000 + rateBp));
  return grossCents - net;
}

/**
 * Verteilt `amount` (>= 0) proportional zu `weights` auf ganze Cent per Largest-Remainder.
 * Rueckgabe hat dieselbe Laenge wie weights und summiert sich EXAKT zu `amount`.
 * Tie-Break: groesserer Restanteil zuerst, dann kleinerer tieKey (rate_bp) — deterministisch.
 */
export function largestRemainder(amount: number, weights: number[], tieKeys: number[]): number[] {
  const total = weights.reduce((s, w) => s + w, 0);
  const out = weights.map(() => 0);
  if (amount <= 0 || total <= 0) return out;

  const rema: { idx: number; frac: number; tie: number }[] = [];
  let assigned = 0;
  weights.forEach((w, i) => {
    const exact = (amount * w) / total;
    const floor = Math.floor(exact);
    out[i] = floor;
    assigned += floor;
    rema.push({ idx: i, frac: exact - floor, tie: tieKeys[i] ?? 0 });
  });
  let rest = amount - assigned;
  rema.sort((a, b) => (b.frac - a.frac) || (a.tie - b.tie));
  for (let k = 0; k < rema.length && rest > 0; k++) {
    const j = rema[k]!.idx;
    out[j] = (out[j] ?? 0) + 1;
    rest--;
  }
  return out;
}

/** Antoine 12.10.2026: Getraenkeanteil einer Menue-Pauschale, wenn die Getraenke keinen Preis tragen. */
export const STANDARD_GETRAENKEANTEIL_BP = 3000;

/** Ein USt-Teil einer Sektions-Pauschale. `perPersonCents` null = nur als Gesamtbetrag darstellbar. */
export interface PauschalTeil {
  rateBp: number;
  grossCents: number;
  perPersonCents: number | null;
  anteil: "speisen" | "getraenke" | null;
  /** Migration 751: fuer so viele Gaeste gilt `perPersonCents` (Menue-Pauschale abzueglich ersetzter Gaeste). */
  gaeste?: number;
}

/** Zeile einer Pauschale-Sektion, die von der Pauschale abgedeckt ist: weder „zubuchbar“ noch „zzgl.“. */
export function inPauschale(it: { isOptional?: boolean | null; surcharge?: boolean | null; menueWahlGruppe?: string | null }): boolean {
  // sql/771: eine Menuewahl-Kopfzeile (Aufpreis je Gast) kommt immer HINZU, wie „zzgl.“ - sie geht nie in einer Pauschale auf.
  return it.isOptional !== true && it.surcharge !== true && it.menueWahlGruppe == null;
}

/**
 * DIE EINE Stelle, die eine Sektions-Pauschale in USt-Teile zerlegt (Engine, Rechnung, LexOffice).
 *
 * Regel (Antoine, 12.10.2026: „Getraenke muessen preislich immer extra angegeben werden. Wenn das
 * nicht gemacht wird, muss 70% Speisen und 30% Getraenke automatisch berechnet werden."):
 * - Menue-Pauschale mit nicht-optionalen Getraenkezeilen (19 %) IM Menue, die einen Preis tragen:
 *   deren Summe ist der Getraenkeanteil (19 %), der Rest der Speisenanteil (Satz der ersten
 *   Speisezeile). Liegt die Getraenkesumme ueber der Pauschale, wird sie auf die Pauschale
 *   gekappt — lieber zu viel 19 % als zu wenig (7 % wird nie zu hoch ausgewiesen), und Versand/
 *   Rechnung bleiben moeglich.
 * - Getraenke ohne Preis (im Menue oder als 0-EUR-Zeilen in einer nicht pauschalen
 *   Getraenke-Sektion): 70/30 ueber `teileSpeisenGetraenke`.
 * - Sonst genau ein Teil zum Referenzsatz — unveraendert wie vor der Regel.
 * Die Teile summieren sich immer EXAKT zur Pauschale. Getraenke erkennt die Engine am Satz 19 %
 * (offer_items hat keine Kategorie-Spalte).
 */
export function pauschalTeile(input: EngineInput, section: string): PauschalTeil[] {
  const sp = input.sectionPricing?.[section];
  if (!sp || !istPauschal(sp.mode)) return [];
  const proVeranstaltung = sp.mode === "total_event";
  const betrag = Math.max(0, sp.totalPricePerPersonCents ?? 0);
  // Migration 751: ersetzende Zubuchungen nehmen der MENUE-Pauschale Gaeste weg (nie unter 0).
  const gaeste = section === "menu" ? Math.max(0, input.optionGuestCount - ersetzteGaeste(input)) : input.optionGuestCount;
  // `total_event`: der eingetragene Betrag IST die Summe. Eine Raummiete aendert sich nicht,
  // wenn zwei Gaeste mehr kommen — genau das kann `total` nicht abbilden.
  const gross = proVeranstaltung
    ? sichereGanzzahl(betrag, `Sektions-Pauschale "${section}" (ganze Veranstaltung)`)
    : sichereGanzzahl(betrag * gaeste, `Sektions-Gesamtpreis "${section}" (pro Person * Gaeste)`);
  // Satz der ERSTEN nicht-optionalen Zeile dieser Sektion (deterministisch), Fallback 700.
  const referenz = input.items.find((it) => it.section === section && inPauschale(it)) ?? input.items.find((it) => it.section === section);
  const einzel: PauschalTeil[] = [{ rateBp: referenz?.vatRateBp ?? 700, grossCents: gross, perPersonCents: proVeranstaltung ? null : betrag, anteil: null }];
  if (section !== "menu") return einzel;
  // `gaeste` nur, wenn es von der Optionszahl abweicht (ersetzte Hauptmenue-Gaeste) - sonst bitgleich zu vorher.
  // Fix 27.10.2026 (Zweitpruefung #1853): auf JEDEM Rueckweg der Menue-Sektion, auch ohne 19-%-Zeile - sonst setzen
  // LexOffice (`z.gaeste ?? optionGuestCount`) und Abrechnung die volle Gaestezahl (25 x 112 statt 22 x 112 EUR).
  const mitGaeste = (teile: PauschalTeil[]): PauschalTeil[] => (gaeste !== input.optionGuestCount ? teile.map((t) => ({ ...t, gaeste })) : teile);

  const kern = input.items.filter((it) => it.section === section && inPauschale(it));
  const speise = kern.find((it) => it.vatRateBp !== GETRAENKE_SATZ_BP);
  if (!speise) return mitGaeste(einzel);
  const getraenkeImMenue = kern.filter((it) => it.vatRateBp === GETRAENKE_SATZ_BP);
  const getraenkeOhnePreisDaneben = !istPauschal(input.sectionPricing?.drinks?.mode)
    && input.items.some((it) => it.section === "drinks" && it.isOptional !== true && it.vatRateBp === GETRAENKE_SATZ_BP && lineGross(it, guestsFor(it, input)) === 0);
  if (getraenkeImMenue.length === 0 && !getraenkeOhnePreisDaneben) return mitGaeste(einzel);

  // Die Menue-PAUSCHALE sinkt fuer ersetzte Gaeste als Ganzes (`gaeste` oben), also auch ihr Getraenkeanteil:
  // die Getraenkezeilen im Menue werden hier mit denselben Gaesten bemessen wie die Pauschale (unveraendert
  // gegenueber vor dem Fix 27.10.2026, obwohl `istHauptmenueZeile` 19-%-Zeilen seither ausnimmt).
  const k = ersetzteGaeste(input);
  // B1-Nachtrag: Gaeste VOR dem Abzug, dann genau einmal k - auch wenn die Zeile als Mitglied eines Speisen-Wahlgangs
  // (`wahlgangIstHauptmenue`) schon selbst k abzoege.
  const ohneK = { ...input, ersetzteHauptmenueGaeste: 0 };
  const bepreist = getraenkeImMenue.reduce((s, it) => s + lineGross(it, Math.max(0, guestsFor(it, ohneK) - k)), 0);
  const teile = teileNachGetraenken(gross, proVeranstaltung ? null : betrag, gaeste, speise.vatRateBp, bepreist);
  return mitGaeste(teile.length > 0 ? teile : einzel);
}

/**
 * Teilt einen Betrag „inkl. Getraenke" in Speisen- und Getraenkeanteil (Menue-Pauschale und Paket).
 * `bepreist` > 0: so viel ist Getraenk (auf `gross` gekappt), sonst 70/30. `betragProKopf` null =
 * nur als Ganzes teilbar; sonst je Gast teilen und mal Gaeste. Teile ohne Betrag fallen weg.
 */
function teileNachGetraenken(gross: number, betragProKopf: number | null, gaeste: number, speiseBp: number, bepreist: number): PauschalTeil[] {
  let teile: PauschalTeil[];
  if (bepreist > 0) {
    const getraenke = Math.min(bepreist, gross);
    const proKopf = betragProKopf != null && gaeste > 0 && getraenke % gaeste === 0;
    teile = [
      { rateBp: speiseBp, grossCents: gross - getraenke, perPersonCents: proKopf ? (gross - getraenke) / gaeste : null, anteil: "speisen" },
      { rateBp: GETRAENKE_SATZ_BP, grossCents: getraenke, perPersonCents: proKopf ? getraenke / gaeste : null, anteil: "getraenke" },
    ];
  } else if (betragProKopf == null) {
    const t = teileSpeisenGetraenke(gross, STANDARD_GETRAENKEANTEIL_BP);
    teile = [
      { rateBp: speiseBp, grossCents: t.speisenCents, perPersonCents: null, anteil: "speisen" },
      { rateBp: GETRAENKE_SATZ_BP, grossCents: t.getraenkeCents, perPersonCents: null, anteil: "getraenke" },
    ];
  } else {
    // Je Gast teilen, dann mal Gaeste: beide Teile bleiben als „x EUR pro Person" darstellbar.
    const t = teileSpeisenGetraenke(betragProKopf, STANDARD_GETRAENKEANTEIL_BP);
    teile = [
      { rateBp: speiseBp, grossCents: t.speisenCents * gaeste, perPersonCents: t.speisenCents, anteil: "speisen" },
      { rateBp: GETRAENKE_SATZ_BP, grossCents: t.getraenkeCents * gaeste, perPersonCents: t.getraenkeCents, anteil: "getraenke" },
    ];
  }
  return teile.filter((t) => t.grossCents > 0);
}

/**
 * Antoine 13.10.2026: die Kopfzeile eines Pakets „inkl. Getraenke" teilt sich bei der USt wie die
 * Menue-Pauschale (`teileNachGetraenken`). Getraenke = nicht-optionale 19-%-Bestandteile desselben
 * Pakets (gleiche `packageId` und gleicher Tag); ihr „eigener Preis" ist `catalogPriceCents`.
 * Kopfzeile = die EINZIGE Zeile des Pakets mit Betrag (Bestandteile stehen auf 0 €, siehe
 * `paketZeilenBauen`). null = nichts zu teilen, die Zeile zaehlt unveraendert zu ihrem Satz.
 * Die Teile summieren sich exakt zu `lineGross(item)`.
 */
export function paketTeile(input: EngineInput, item: EngineItem): PauschalTeil[] | null {
  if (!item.packageId || item.isOptional === true || item.section === ADJUSTMENT_SECTION) return null;
  // sql/771: der Aufpreis einer Menuewahl-Kopfzeile zaehlt zu ihrem eigenen Satz, er teilt sich nicht nach Getraenken.
  if (item.menueWahlGruppe != null) return null;
  if (istPauschal(input.sectionPricing?.[item.section]?.mode)) return null;
  const gaeste = guestsFor(item, input);
  const gross = lineGross(item, gaeste);
  if (gross <= 0) return null;
  // Je Gast teilbar nur bei genau einem Paket je Gast; sonst (flat, Menge != 1) als Ganzes.
  const proKopf = item.priceMode === "per_person" && item.qtyMilli === 1000 ? item.unitPriceCents : null;
  // Punkt R: am Paket hinterlegte Aufteilung hat Vorrang vor der Heuristik — Speisen immer 7 %.
  // Steuerfreie Zeile (0 %, z. B. Paragraf 19) teilt nie — sonst entstuende USt, die es nicht gibt.
  if (item.drinksShareCents != null) {
    if (item.drinksShareCents <= 0 || item.vatRateBp === 0) return null;
    const anteil = lineGross({ ...item, unitPriceCents: item.drinksShareCents }, gaeste);
    const teile = teileNachGetraenken(gross, proKopf, gaeste, SPEISEN_SATZ_BP, anteil);
    return teile.length > 0 ? teile : null;
  }
  if (item.vatRateBp === GETRAENKE_SATZ_BP) return null;
  // sql/771: Menuewahl-Kopfzeilen (Aufpreise) gehoeren nicht zur Paket-Kopfzeile; ihre Bestandteile zaehlen mit den Gaesten
  // ihres Menues (`menuewahlGaesteAus` legt sie in `wahlGaeste`), nicht gewaehlte Menues mit 0.
  const gruppe = input.items.filter((it) => it !== item && it.packageId === item.packageId && (it.dayNo ?? null) === (item.dayNo ?? null) && it.isOptional !== true && it.menueWahlGruppe == null);
  if (gruppe.some((it) => lineGross(it, guestsFor(it, input)) !== 0)) return null;
  const getraenke = gruppe.filter((it) => it.vatRateBp === GETRAENKE_SATZ_BP);
  if (getraenke.length === 0) return null;
  // Fix 27.10.2026 (N1): ein 19-%-Bestandteil ist seit `istHauptmenueZeile` ohne 19 % keine Hauptmenue-Zeile mehr und
  // behielte alle Gaeste - die Kopfzeile sinkt aber fuer ersetzte Gaeste. Der Getraenkeanteil wird darum mit den
  // Gaesten bemessen, mit denen die Kopfzeile rechnet (ersetzte Gaeste abgezogen, wie in `pauschalTeile`).
  const k = istHauptmenueZeile(item) ? ersetzteGaeste(input) : 0;
  const ohneK = { ...input, ersetzteHauptmenueGaeste: 0 };
  const bepreist = getraenke.reduce((s, it) => s + lineGross({ ...it, unitPriceCents: Math.max(0, it.catalogPriceCents ?? 0) }, item.guestCount != null ? gaeste : Math.max(0, guestsFor(it, ohneK) - k)), 0);
  const teile = teileNachGetraenken(gross, proKopf, gaeste, item.vatRateBp, bepreist);
  return teile.length > 0 ? teile : null;
}

const GETRAENKE_SATZ_BP = 1900;
const SPEISEN_SATZ_BP = 700;

/**
 * Rechnet die vollstaendige Preisaufschluesselung einer Option.
 * Reihenfolge (Spec D): Zwischensumme -> Rabatt (nur discountable, nicht Anpassung)
 * -> Anpassung -> Endpreis. USt je Satz auf den finalen Brutto-Betrag pro Bucket.
 */
export function computeBreakdown(roh: EngineInput): PriceBreakdown {
  // Migration 751: ersetzende Zubuchungen senken die Gaestezahl des Hauptmenues. Ausdruecklich
  // uebergeben (Rechnung) hat Vorrang, sonst aus den gebuchten Zubuchungen abgeleitet.
  const mitK: EngineInput = roh.ersetzteHauptmenueGaeste != null
    ? roh
    : { ...roh, ersetzteHauptmenueGaeste: ersetzteGaesteAus(roh.items, roh.gebuchteZusatzMengen, roh.optionGuestCount) };
  // B1-Nachtrag: Wahlgang-Mitglieder senken ersetzte Gaeste nach dem Flag ihrer Gruppe (`wahlgangIstHauptmenue`).
  const mitG: EngineInput = mitK.wahlHauptmenue ? mitK : { ...mitK, wahlHauptmenue: wahlgangHauptmenueKarte(mitK.items) };
  // Migration 755: Wahlgaenge mengengenau. Leer (kein bepreister Wahlgang) = Eingabe unveraendert, bitgleich.
  const wahlBasis = mitG.wahlGaeste ?? wahlGaesteAus(mitG.items, mitG);
  // sql/771: Menuewahl im Paket. Leer (keine Gruppe mit mindestens zwei Menues) = bitgleich zu vorher.
  const menue = menuewahlGaesteAus(mitG.items, mitG);
  const wahl = menue.size > 0 ? new Map([...wahlBasis, ...menue]) : wahlBasis;
  const input: EngineInput = wahl.size > 0 ? { ...mitG, wahlGaeste: wahl } : mitG;
  const lineTotals: LineTotal[] = [];
  let subtotalCents = 0;
  let adjustmentCents = 0;
  /**
   * Waechst an GENAU DENSELBEN zwei Stellen wie `subtotalCents` (siehe unten). Das ist die
   * eigentliche Absicherung: die Invariante "Summe der Abschnitte == Zwischensumme" ist damit
   * nicht getestet, sondern strukturell wahr — wer kuenftig einen Sonderfall bei der
   * Zwischensumme einbaut, kommt an der Abschnittssumme nicht vorbei. Eine Map statt eines
   * Arrays, damit die Einfuegereihenfolge erhalten bleibt (deterministische Ausgabe).
   */
  const sectionGross = new Map<string, number>();
  const zuAbschnitt = (section: string, gross: number): void => {
    sectionGross.set(section, (sectionGross.get(section) ?? 0) + gross);
  };

  // Brutto je Bucket (rate_bp) sowie der discountable Nicht-Anpassungs-Anteil je Bucket.
  const grossByBucket = new Map<number, number>();
  const discountableByBucket = new Map<number, number>();
  let discountBase = 0;

  // KONZEPT-GESAMTPREIS-PRO-MENUE, verallgemeinert: in einer Sektion mit 'total' zaehlt keine
  // einzelne (nicht optionale) Zeile dieser Sektion mehr fuer sich - ihr lineTotal zeigt 0 (die
  // Kundenansicht zeigt dadurch keinen Positions-Einzelpreis mehr), stattdessen zaehlt EIN
  // zusammengefasster Betrag weiter unten, je Sektion. Optionale Zusatzpositionen (isOptional)
  // bleiben unberuehrt - sie haben einen eigenen, vom Kunden gewaehlten Preis, der nichts mit
  // dem fixen Sektionspreis zu tun hat.
  const sectionPricing = input.sectionPricing ?? {};
  const istSektionsKern = (item: EngineItem): boolean => istPauschal(sectionPricing[item.section]?.mode) && inPauschale(item);

  for (const item of input.items) {
    const kollabiert = istSektionsKern(item);
    const gross = kollabiert ? 0 : lineGross(item, guestsFor(item, input));
    lineTotals.push({ id: item.id, grossCents: gross });
    /*
     * H-21 / PUNKT 32: optionale Positionen sind reine Anzeige-Zeilen — **bis der Gast sie
     * anhakt.** Dann zaehlen sie in Zwischensumme und USt-Eimer, aber AUSDRUECKLICH NICHT in die
     * Rabattbasis.
     *
     * WARUM KEIN RABATT (Antoines Entscheidung vom 16.09.2026, woertlich: „Nein, Angebotsrabatt
     * gilt nicht fuer Zubuchungen"): der Gast hakt einen konkreten Preis an — „Flasche Bollinger
     * 89,00 EUR" — und die Summe muss sich um exakt diesen Betrag bewegen. Ein anteiliger Rabatt
     * darauf machte die angezeigte Zahl zur Luege.
     *
     * DIE MULTIPLIKATION STEHT HIER und nicht beim Aufrufer. Sonst gaebe es zwei Stellen, an
     * denen aus Menge und Preis ein Betrag wird — und die zweite waere die SQL-Seite. Genau die
     * zweite Wahrheit, gegen die dieses ganze Konzept argumentiert.
     */
    if (item.isOptional === true) {
      const menge = input.gebuchteZusatzMengen?.get(item.id ?? "");
      if (!menge || menge <= 0) continue; // nicht gebucht -> unveraendert reine Anzeige
      // Fix 27.10.2026 (V-1262): eine ERSETZENDE `per_person`-Zubuchung zaehlt Gaeste - Betrag = Zeile fuer k Gaeste
      // (k = gebuchte Menge, gedeckelt auf die Gaeste der Zeile). Nicht ersetzende `per_person` bleiben „fuer alle“.
      // sql/763: der Gaeste-Zaehler einer NICHT ersetzenden per_person-Zubuchung laeuft nicht hier, sondern ueber
      // `zubuchungenEinrechnen` (apps/api lib/lexoffice.ts: Teilgruppe = k) - dieser Zweig bleibt „fuer alle“.
      const gebucht = item.priceMode === "per_person"
        ? (item.addonErsetztHauptmenue === true ? lineGross(item, Math.min(Math.floor(menge), guestsFor(item, input))) : gross)
        : sichereGanzzahl(gross * menge, "Zubuchungs-Menge");
      subtotalCents += gebucht;
      zuAbschnitt(item.section, gebucht);
      grossByBucket.set(item.vatRateBp, (grossByBucket.get(item.vatRateBp) ?? 0) + gebucht);
      continue;
    }
    if (kollabiert) continue; // zaehlt unten gebuendelt, nicht als Einzelzeile.
    // Paket inkl. Getraenke: dieselbe Zeile, aber zu zwei Saetzen (Summe exakt `gross`).
    const eimerTeile = paketTeile(input, item) ?? [{ rateBp: item.vatRateBp, grossCents: gross }];
    for (const t of eimerTeile) grossByBucket.set(t.rateBp, (grossByBucket.get(t.rateBp) ?? 0) + t.grossCents);

    const isAdjustment = item.section === ADJUSTMENT_SECTION;
    if (isAdjustment) {
      adjustmentCents += gross;
    } else {
      subtotalCents += gross;
      zuAbschnitt(item.section, gross);
      // N-G (Bughunt 2026-07-11): negative Positionen (z. B. Gutschrift-/Abzugszeilen) zaehlen
      // NICHT zur rabattfaehigen Basis. Sonst verzerrt ein negatives Gewicht die proportionale
      // USt-Aufteilung je Satz (Largest-Remainder ist nur fuer Gewichte >= 0 summenexakt).
      const discountable = item.discountable !== false && gross > 0;
      if (discountable) {
        discountBase += gross;
        for (const t of eimerTeile) discountableByBucket.set(t.rateBp, (discountableByBucket.get(t.rateBp) ?? 0) + t.grossCents);
      }
    }
  }

  // Je Sektion mit 'total' EINEN zusammengefassten Betrag einrechnen (deterministische
  // Reihenfolge: Objektschluessel in der Reihenfolge, in der sie in sectionPricing stehen).
  for (const section of Object.keys(sectionPricing)) {
    for (const teil of pauschalTeile(input, section)) {
      const gross = teil.grossCents;
      subtotalCents += gross;
      zuAbschnitt(section, gross);
      grossByBucket.set(teil.rateBp, (grossByBucket.get(teil.rateBp) ?? 0) + gross);
      // Wie jede normale Zeile discountable (kein eigenes Flag noetig - der Gesamtpreis ersetzt
      // die Summe der Positions-Zeilen, die alle discountable=true als Default tragen).
      if (gross > 0) {
        discountBase += gross;
        discountableByBucket.set(teil.rateBp, (discountableByBucket.get(teil.rateBp) ?? 0) + gross);
      }
    }
  }

  // Rabatt bestimmen (auf die discountable Basis begrenzt; nie negativ).
  let discountCents = 0;
  if (input.discountPercentBp && input.discountPercentBp > 0) {
    discountCents = Math.round((discountBase * input.discountPercentBp) / 10000);
  } else if (input.discountAmountCents && input.discountAmountCents > 0) {
    discountCents = input.discountAmountCents;
  }
  discountCents = Math.max(0, Math.min(discountCents, discountBase));

  // Rabatt proportional zum discountable Brutto je Bucket verteilen (Largest-Remainder).
  const buckets = [...grossByBucket.keys()].sort((a, b) => a - b);
  // N-G: Gewichte defensiv auf >= 0 klemmen (die Basis oben ist bereits negativ-frei).
  const discWeights = buckets.map((b) => Math.max(0, discountableByBucket.get(b) ?? 0));
  const discShares = largestRemainder(discountCents, discWeights, buckets);

  const vatBreakdown: VatBucket[] = [];
  let totalCents = 0;
  buckets.forEach((rateBp, i) => {
    const grossFinal = (grossByBucket.get(rateBp) ?? 0) - (discShares[i] ?? 0);
    const vatCents = vatFromGross(grossFinal, rateBp);
    vatBreakdown.push({ rateBp, grossCents: grossFinal, vatCents, netCents: grossFinal - vatCents });
    totalCents += grossFinal;
  });

  const perPersonCents = proPersonCents(totalCents, input.optionGuestCount) ?? 0;

  return {
    subtotalCents,
    discountCents,
    discountBaseCents: discountBase,
    adjustmentCents,
    amountTotalCents: totalCents,
    perPersonCents,
    vatBreakdown,
    lineTotals,
    // Abschnitte ohne Betrag weglassen: eine Zwischenueberschrift mit 0,00 EUR sieht aus wie ein
    // vergessener Preis, obwohl der Abschnitt schlicht leer ist.
    sectionTotals: [...sectionGross.entries()].filter(([, cents]) => cents !== 0).map(([section, grossCents]) => ({ section, grossCents })),
  };
}

/**
 * Berechnet den Einzelpreis (flat) einer Anpassungszeile, sodass der Endpreis der Option den
 * Zielpreis trifft: adjustment = target - (aktueller Endpreis OHNE bestehende Anpassung).
 * Der Aufrufer legt damit die (max. eine) section='adjustment'-Zeile an/aktualisiert sie.
 */
export function targetPriceAdjustmentCents(input: EngineInput, targetCents: number): number {
  const withoutAdjustment: EngineInput = {
    ...input,
    items: input.items.filter((it) => it.section !== ADJUSTMENT_SECTION),
  };
  const base = computeBreakdown(withoutAdjustment).amountTotalCents;
  return targetCents - base;
}

/* -------------------- Rundungsregel (Lernen S4) -------------------- */

/**
 * LERNEN S4 (Antoine, 27.09.2026; Konzept ~/maestro-uebergabe/konzept-lernen.html): „Du rundest den
 * Menuepreis pro Gast auf volle 5 EUR auf“ wird eine ECHTE Einstellung, die diese Engine ausfuehrt —
 * nie eine Regel fuer das Modell. Das Modell liefert weiter nur Katalog-Kennungen und Mengen.
 *
 * Angewandt wird sie nur auf frisch komponierte KI-Entwuerfe (lib/kompositions-lauf.ts,
 * lib/inquiry-auto-draft.ts), nie beim Neuberechnen bestehender Angebote: ein gesendetes Angebot
 * darf seinen Betrag nicht aendern, weil jemand spaeter eine Einstellung setzt.
 */
export interface PreisRundung {
  /** 100 = volle Euro, 500 = volle 5 EUR, 1000 = volle 10 EUR. */
  schrittCents: 100 | 500 | 1000;
  richtung: "auf" | "ab";
}

export const PREIS_RUNDUNGS_SCHRITTE = [100, 500, 1000] as const;

export interface RundungsZeile {
  section: string;
  priceMode: string;
  qtyMilli: number;
  unitPriceCents: number;
  isOptional?: boolean | null;
  /** Mitglied einer Wahlgruppe (Migration 384) — traegt nie die Rundung. */
  choiceGroupId?: string | null;
  /** Teil eines Pakets (Kopf- oder Bestandteilzeile) — traegt nie die Rundung. */
  packageId?: string | null;
  /** sql/755: der Gang gehoert zur Wahlgang-Definition (`wahlgaengeVerzeichnis`). */
  courseLabel?: string | null;
  /** „zzgl.“-Zeile einer Pauschal-Sektion (Migration 722) - zaehlt nicht als in der Pauschale aufgegangen. */
  surcharge?: boolean | null;
}

/** sql/755: wie die Option Wahlgaenge rechnet (`offer_options.wahl_mengengenau`). Fehlt = wie vor der Migration. */
export interface RundungsOptionen {
  wahlMengengenau?: boolean;
  /**
   * Vierte Pruefrunde (Punkt 5): die Ausnahmen der Engine (`wahlGaesteAus`) - ohne sie wuerde der „Preis pro Gast“
   * eine Alternative auslassen, die die Engine voll zaehlt. Fehlen die Angaben, gelten sie als nicht zutreffend.
   * `optionGuestCount`/`guestCountByDay`: ueber `WAHL_MAX_GAESTE` rechnet die Engine wie vor sql/755.
   * `sectionPricing`: eine Pauschal-Sektion zaehlt ihre Gerichte nicht einzeln.
   */
  optionGuestCount?: number;
  guestCountByDay?: Record<string, number>;
  sectionPricing?: Record<string, { mode?: string | null }>;
}

/**
 * sql/755 (dritte Pruefrunde): Indizes der Wahlgang-Alternativen, die im Grundbetrag NICHT zaehlen. Bei einer
 * mengengenauen Option traegt ohne Zuordnung die guenstigste Alternative alle Gaeste (`wahlGaesteAus`) - der
 * „Preis pro Gast“ zaehlt darum nur sie, nicht jede Alternative. Dieselbe Definition wie die Engine.
 */
function nichtImGrundbetrag(items: readonly RundungsZeile[], opts?: RundungsOptionen): Set<number> {
  const raus = new Set<number>();
  if (opts?.wahlMengengenau !== true) return raus;
  // Wie `wahlGaesteAus`: keine Gaeste oder zu viele Gaeste -> die Engine rechnet wie vor sql/755 (jede Alternative voll).
  if (opts.optionGuestCount !== undefined && (opts.optionGuestCount < 1 || groessteGaestezahl({ optionGuestCount: opts.optionGuestCount, guestCountByDay: opts.guestCountByDay }) > WAHL_MAX_GAESTE)) return raus;
  const positionen = items.map((it, i) => ({ id: String(i), section: it.section, isOptional: it.isOptional, courseLabel: it.courseLabel, choiceGroupId: it.choiceGroupId, priceMode: it.priceMode, qtyMilli: Number(it.qtyMilli) }));
  for (const w of wahlgaengeVerzeichnis(positionen)) {
    const idx = w.ids.map(Number);
    if (idx.some((i) => items[i]!.packageId)) continue;
    // Pauschal-Sektion: die Gerichte stecken im Pauschalpreis, die Engine zaehlt sie nicht einzeln.
    if (idx.some((i) => istPauschal((opts.sectionPricing?.[items[i]!.section]?.mode as string | undefined) ?? null) && inPauschale({ isOptional: items[i]!.isOptional ?? false, surcharge: items[i]!.surcharge ?? false }))) continue;
    // Ohne bepreiste Alternative bleibt die Engine bei „wie bisher“.
    if (!idx.some((i) => Number(items[i]!.unitPriceCents) > 0)) continue;
    let guenstigste = idx[0]!;
    for (const i of idx) if (Number(items[i]!.unitPriceCents) < Number(items[guenstigste]!.unitPriceCents)) guenstigste = i;
    for (const i of idx) if (i !== guenstigste) raus.add(i);
  }
  return raus;
}

/**
 * Menuepreis pro Gast: Summe der Pro-Person-Zeilen (ohne Optionen und ohne Anpassungszeile), vom
 * Server gerechnete Einzelpreise. EINE Definition fuer die Rundungsregel UND fuer die Erkennung des
 * Preis-Musters (apps/api/src/lib/lern-muster.ts#preisProGast) — sonst rundete die Regel auf etwas
 * anderes, als das Muster gezaehlt hat.
 */
export function menuePreisProGastCents(items: readonly RundungsZeile[], opts?: RundungsOptionen): number {
  const raus = nichtImGrundbetrag(items, opts);
  return Math.round(
    items
      .filter((it, i) => it.priceMode === "per_person" && it.isOptional !== true && it.section !== ADJUSTMENT_SECTION && !raus.has(i))
      .reduce((s, it) => s + sichereGanzzahl(Number(it.unitPriceCents) * Number(it.qtyMilli), "Rundung (Einzelpreis * Menge)") / 1000, 0),
  );
}

export interface RundungsErgebnis {
  /** Index der Zeile in `items`, deren Einzelpreis sich aendert. */
  index: number;
  neuerEinzelpreisCents: number;
  vorherProGastCents: number;
  nachherProGastCents: number;
}

/**
 * Rechnet, wie die Regel den Menuepreis pro Gast trifft — so, wie der Betreiber es von Hand tat:
 * EIN Einzelpreis aendert sich um den Unterschied. Traeger ist die teuerste Menue-Zeile (`section`
 * menu, pro Person, genau eine Portion je Gast, keine Wahlgruppe, kein Paket) — so landet der
 * Unterschied cent-genau im Preis pro Gast und im USt-Satz der Speisen, und das Preisverhaeltnis von
 * Alternativen bleibt unberuehrt.
 *
 * `null` (Entwurf bleibt unveraendert, statt dass ein Betrag geraten wird), wenn
 *   - nichts zu tun ist (schon rund, kein Menuepreis) oder der Schritt unbekannt ist;
 *   - eine zaehlende Zeile keine ganze Portion je Gast hat (dann rundet die Engine je Zeile anders
 *     und „Preis pro Gast 85,00 €“ stimmte nicht auf den Cent);
 *   - keine Zeile ihn tragen kann;
 *   - Abrunden auf 0 € faellt oder den Traeger um mehr als die Haelfte senkt (Zweitpruefung M1).
 */
export function preisRundungAnwenden(items: readonly RundungsZeile[], regel: PreisRundung, opts?: RundungsOptionen): RundungsErgebnis | null {
  if (!(PREIS_RUNDUNGS_SCHRITTE as readonly number[]).includes(regel.schrittCents)) return null;
  const zaehlt = items.filter((it) => it.priceMode === "per_person" && it.isOptional !== true && it.section !== ADJUSTMENT_SECTION);
  if (zaehlt.some((it) => Number(it.qtyMilli) % 1000 !== 0)) return null;
  const vorher = menuePreisProGastCents(items, opts);
  if (vorher <= 0 || vorher % regel.schrittCents === 0) return null;
  const nachher = regel.richtung === "auf" ? Math.ceil(vorher / regel.schrittCents) * regel.schrittCents : Math.floor(vorher / regel.schrittCents) * regel.schrittCents;
  if (nachher <= 0) return null;
  const delta = nachher - vorher;
  let index = -1;
  items.forEach((it, i) => {
    if (it.section !== "menu" || it.priceMode !== "per_person" || it.isOptional === true || Number(it.qtyMilli) !== 1000 || it.choiceGroupId || it.packageId) return;
    if (index < 0 || Number(it.unitPriceCents) > Number(items[index]!.unitPriceCents)) index = i;
  });
  if (index < 0) return null;
  const alt = Number(items[index]!.unitPriceCents);
  const neu = alt + delta;
  if (neu <= 0 || neu * 2 < alt) return null;
  return { index, neuerEinzelpreisCents: sichereGanzzahl(neu, "Rundung (neuer Einzelpreis)"), vorherProGastCents: vorher, nachherProGastCents: nachher };
}

/* -------------------- Nullpreis-Deckung (§5.9 Chip / §5.13 Versandsperre) -------------------- */

/**
 * §5.9 + §5.13 (docs/KONZEPT-ANFRAGE-ZU-ANGEBOT.md) — wann eine 0-EUR-Position ein Defekt ist.
 *
 * Vor dem Wurzelfix (§5.1, PR #410) war eine 0-EUR-Position mehrdeutig: Menue-Gaenge stehen im
 * Katalog bewusst auf 0, weil der Preis am Paket haengt — nur kam der Paketpreis nie im Angebot
 * an (Referenzfall: 10 von 11 Positionen zu 0,00 EUR). Seit §5.1 traegt die bepreiste
 * **Paket-Kopfzeile** den Preis und ihre Bestandteile stehen mit demselben `packageId` auf 0
 * („im Paketpreis enthalten"). Damit ist die Zweideutigkeit weg:
 *
 *   0 EUR **mit** bepreister Kopfzeile desselben Pakets  -> gedeckt, legitim.
 *   0 EUR **ohne** solche Deckung                        -> echte Preisluecke.
 *
 * Optionale Positionen bleiben aussen vor: sie sind ein unverbindlicher Zusatz, den der Kunde
 * erst waehlt — ein Preis von 0 ist dort eine Aussage, kein Defekt.
 *
 * Reine Funktion ueber PERSISTIERTE Werte (kein Modell, keine Eingabe, keine Summenrechnung) —
 * Invariante „Betraege kommen nie vom Modell" bleibt unberuehrt.
 *
 * Deckung wird je Variante geprueft (eine Variante ist die versendbare Einheit); die Kopfzeile
 * ist an `catalogItemId === null` erkennbar — genau so legt `paket-anwenden.ts` sie an.
 *
 * WARUM HIER und nicht in `apps/api`: dieselbe Regel entscheidet zwei Dinge — ob der Versand
 * blockiert (`apps/api/src/routes/offers.ts`) und ob der Builder den Warn-Chip „kein Preis" zeigt
 * (`apps/web/src/pages/builder.tsx`). `apps/web` kann nicht aus `apps/api` importieren; zwei
 * Kopien wuerden auseinanderlaufen und die Oberflaeche wuerde luegen. Dasselbe Prinzip wie bei
 * `recompute()` in §5.14: EINE Engine, keine Kopie.
 */
export interface NullpreisKandidat {
  /** Variante (offer_option), zu der die Zeile gehoert — Deckung gilt nur innerhalb einer Variante. */
  optionId: string;
  /** Paketbezug der Zeile; null = kein Paket, also nichts, was decken koennte. */
  packageId: string | null;
  isOptional: boolean;
  unitPriceCents: number;
  /** null = keine Katalogverknuepfung; bei einer Paketzeile heisst das: Kopfzeile. */
  catalogItemId: string | null;
  /**
   * KONZEPT-GESAMTPREIS-PRO-MENUE: Sektion der Zeile. Nur fuer die Gesamtpreis-Deckung gebraucht.
   * Optional, damit bestehende Aufrufer ohne dieses Feld unveraendert weiterlaufen — fehlt es,
   * verhaelt sich die Funktion exakt wie vor dieser Erweiterung.
   */
  section?: string;
}

export function ungedeckteNullpreisPositionen<T extends NullpreisKandidat>(
  items: readonly T[],
  /**
   * KONZEPT-GESAMTPREIS-PRO-MENUE, generalisiert 28.08.2026 (Bughunt, PR #757/#758-Nachzug):
   * Schluessel `` `${optionId}::${section}` `` je Sektion einer Option, die im Gesamtpreis-Modus
   * laeuft — Menue GENAUSO wie Getraenke/Equipment/Personal/Sonstiges. Deren Zeilen (section
   * passend, nicht optional) gelten als gedeckt, unabhaengig vom Einzelpreis — der tatsaechliche
   * Preis steht an der Sektions-Pauschale, nicht an der Zeile. Weggelassen = kein Unterschied zum
   * Bestandsverhalten (analog zur Paket-Deckung oben). VORHER war das ausschliesslich auf
   * `section === "menu"` beschraenkt (bare optionId-Set) — die Luecke bei den anderen Sektionen
   * liess die "kein Preis"-Badge der "zaehlt nicht"-Badge im Client widersprechen.
   */
  totalModeSectionKeys?: ReadonlySet<string>,
): T[] {
  // Bepreiste Kopfzeilen je Variante+Paket einsammeln — sie sind der Deckungsnachweis.
  const gedeckt = new Set<string>();
  for (const it of items) {
    if (it.packageId && it.catalogItemId === null && it.unitPriceCents > 0) {
      gedeckt.add(`${it.optionId}::${it.packageId}`);
    }
  }
  return items.filter((it) => {
    if (it.unitPriceCents !== 0 || it.isOptional) return false;
    // Ohne packageId gibt es nichts, was decken koennte; mit packageId muss die Kopfzeile da sein.
    if (it.packageId && gedeckt.has(`${it.optionId}::${it.packageId}`)) return false;
    if (it.section && totalModeSectionKeys?.has(`${it.optionId}::${it.section}`)) return false;
    return true;
  });
}

/**
 * ID-Sicht auf dieselbe Regel — fuer die Anzeige, die je Zeile nur ein Ja/Nein braucht.
 *
 * Bewusst KEINE zweite Regel, sondern eine Projektion: der Builder soll die Deckungslogik gar
 * nicht kennen, sondern nur fragen „steht diese Zeile drin?". Wichtig ist, dass der Aufrufer die
 * VOLLSTAENDIGE Positionsliste der Variante uebergibt (auch Zeilen anderer Tage), sonst fehlt
 * moeglicherweise die deckende Kopfzeile und es entstuende ein Fehlalarm.
 */
export function ungedeckteNullpreisIds<T extends NullpreisKandidat & { id: string }>(
  items: readonly T[],
  totalModeSectionKeys?: ReadonlySet<string>,
): Set<string> {
  return new Set(ungedeckteNullpreisPositionen(items, totalModeSectionKeys).map((it) => it.id));
}

/* -------------------- Nullpreis im KATALOG (§5.7: was der Import meldet) -------------------- */

/**
 * §5.7 (docs/KONZEPT-ANFRAGE-ZU-ANGEBOT.md) — dieselbe Frage wie oben, eine Ebene frueher.
 *
 * `ungedeckteNullpreisPositionen()` urteilt ueber ANGEBOTS-Zeilen: dort deckt eine bepreiste
 * Paket-Kopfzeile. Im KATALOG gibt es diese Kopfzeile noch nicht — die Position steht allein da,
 * und die einzige Auskunft darueber, ob ihr fehlender Preis Absicht ist, ist ihr Vermerk
 * (`catalog_items.price_note`). Genau den setzt der Import: ein Menue-Gang bekommt
 * „Im Menuepreis enthalten", weil sein Preis am Menue haengt (menu-imports.ts, `commitToCatalog`).
 *
 *   0 EUR **mit** Vermerk „im Menue-/Paketpreis enthalten"  -> gedeckt, legitim.
 *   0 EUR **ohne** solchen Vermerk                          -> echte Preisluecke.
 *
 * WARUM DAS HIER STEHT und nicht als zweite Regel neben der Import-Route: ueber diese eine Frage
 * urteilen inzwischen DREI Stellen — die Rueckmeldung direkt nach dem Import (§5.7), der
 * Zustandsbefund `katalog_ohne_preis` (`apps/api/src/routes/metrics.ts`) und mittelbar der
 * Versand-Riegel, sobald so eine Position in ein Angebot wandert. Widersprechen sie sich, meldet
 * der Import „alles in Ordnung" und die Befunde „12 Positionen ohne Preis" — fuer dieselben Daten.
 * Danach glaubt der Betrieb keiner der beiden Zahlen mehr. Dieselbe Begruendung wie oben, derselbe
 * Ort, dieselbe Ratsche (`apps/api/test/nullpreis-deckung-eine-regel.test.ts`).
 *
 * WICHTIG (aus dem Konzept, woertlich): Menue-Gaenge mit diesem Vermerk sind KORREKT und duerfen
 * nicht als Fehler erscheinen — „sonst erzeugt die Warnung Misstrauen gegen richtige Daten".
 *
 * Reine Funktion ueber PERSISTIERTE Werte. Sie rechnet nichts und erfindet nichts.
 */
export interface KatalogNullpreisKandidat {
  /** `catalog_items.price_cents` — Brutto-Cent, 0 = kein Preis hinterlegt. */
  priceCents: number;
  /** `catalog_items.price_note` — der Vermerk, der einen fehlenden Preis erklaeren kann. */
  priceNote: string | null;
}

/**
 * Sagt der Vermerk aus, dass der Preis WOANDERS steht (am Menue/Paket)?
 *
 * Bewusst eng: verlangt wird beides — ein Preisbegriff („Menuepreis"/„Paketpreis"/„Menüpreis")
 * UND das Wort „enthalten". „Tagespreis" oder „auf Anfrage" erklaeren zwar, warum kein Betrag
 * dasteht, aber sie verlagern ihn nicht — so eine Position landet mit 0,00 EUR im Angebot und
 * bleibt darum eine Luecke. Die Schreibweisen decken die vom Import gesetzte Form
 * („Im Menuepreis enthalten") und die im Komponisten-Prompt genannte („im Paketpreis enthalten")
 * ab, umlaut- und gross-/kleinschreibungstolerant.
 */
export function preisStehtAmPaket(priceNote: string | null | undefined): boolean {
  if (!priceNote) return false;
  const s = priceNote.toLowerCase().replace(/ü/g, "ue").replace(/\s+/g, " ");
  return s.includes("enthalten") && (s.includes("menuepreis") || s.includes("menupreis") || s.includes("paketpreis"));
}

export function ungedeckteNullpreisKatalogPositionen<T extends KatalogNullpreisKandidat>(items: readonly T[]): T[] {
  return items.filter((it) => it.priceCents <= 0 && !preisStehtAmPaket(it.priceNote));
}

/* -------------------- Paket-Preisart: Pauschale oder Preis je Gast -------------------- */

/*
 * Liegt seit Buchungsstrecke D4 in `paket-preisart.ts` (eigene Datei, damit `spanne.ts` sie ohne
 * Kreisimport ueber `index.ts` nutzen kann). Hier unveraendert weitergereicht.
 */
export { paketPreisart } from "./paket-preisart";
export type { PaketPreisQuelle, PaketPreisart } from "./paket-preisart";

/* -------------------- Zahlungsstaffel (Z1.1/Z1.3): aus Stufen werden Betraege -------------------- */

/**
 * Die freie Zahlungsstaffel (GATE-Z1) liegt in einer eigenen Datei, weil sie eine andere Frage
 * beantwortet als die Preis-Engine: nicht „was kostet das", sondern „in welchen Raten wird das
 * bezahlte Ganze eingefordert". Sie wird hier weitergereicht, damit `apps/api` (Route) und
 * `apps/web` (Eingabemaske) dieselbe eine Rechenstelle benutzen.
 */
export { berechneStaffel, ersteRueckwaertsStufe, stufeSollCents, verteileNeu, OFFENE_ZAHLUNGSSTATUS, istOffenerZahlungsstatus } from "./zahlungsstaffel";
export type { Staffelstufe, Staffelergebnis, StaffelFehler } from "./zahlungsstaffel";
export type {
  Neuverteilungszeile,
  Neuverteilungsaenderung,
  Neuverteilungsergebnis,
  NeuverteilungFehler,
} from "./zahlungsstaffel";

/**
 * Zentrale EUR-Anzeige-Formatierer (BUGHUNT-2026-08-15 Q9) — EINE Stelle statt ~8 Kopien.
 * Reine Anzeige; gerechnet wird ausschliesslich in der Engine oben.
 */
export { euro, euroZahl, type AnzeigeSprache } from "./format";

/* -------------------- Varianten-Sortierung: immer nach Preis, kein Buchstabe -------------------- */

/**
 * Antoine, 11.09.2026 (Anlass: eine leere, frisch angelegte Variante sprang durch den frueheren
 * automatischen Buchstaben auf Position "A" und verdraengte eine bereits versendete, teurere
 * Variante - ein Klick auf die vermeintlich neue Variante traf dadurch versehentlich die falsche):
 * "sortier es immer nach dem Preis und nie nach einer Bezeichnung, lasse ABC etc. weg." Varianten
 * haben ab jetzt KEINE automatisch vergebene Kennung mehr - nur die Preis-Sortierung (guenstigste
 * zuerst) bleibt, plus der optionale, vom Betreiber selbst gesetzte `title`. Ohne eigenen Titel
 * zeigt die Oberflaeche ersatzweise den formatierten Gesamtpreis der Variante (z. B. "720,00 €",
 * siehe `euro()` oben) statt eines Buchstabens - genau Antoines Formulierung "der Name ist die
 * Summe im Hintergrund unsichtbar".
 *
 * Vorherige Fassung (bis 11.09.2026) berechnete zusaetzlich einen Buchstaben (`variantenBuchstabe`/
 * `berechneVariantenLabels`) als reine Funktion dieses Preis-Rangs - ENTFERNT, keine Aufrufstelle
 * mehr im Repo (betroffene Anzeige-Fallbacks nutzen jetzt `title || euro(amountTotalCents)`, s.
 * `apps/web/src/ui/VariantenLeiste.tsx`, `apps/web/src/pages/offer-public.tsx` u. a.).
 */
export interface VarianteLabelKandidat {
  id: string;
  amountTotalCents: number;
  sortOrder: number;
}

/**
 * Sortiert wie die `orderBy`-Stellen in `offers.ts`/das `options`-useMemo in `builder.tsx`
 * (amountTotalCents, dann sortOrder), zusaetzlich `id` als dritte, deterministische Tiebreak-Stufe.
 */
export function sortiereVariantenNachPreis<T extends VarianteLabelKandidat>(varianten: readonly T[]): T[] {
  return [...varianten].sort(
    (a, b) => a.amountTotalCents - b.amountTotalCents || a.sortOrder - b.sortOrder || a.id.localeCompare(b.id),
  );
}

/*
 * WARTESCHLANGE Punkt 45 — die Zahlungsvereinbarung eines Angebots.
 *
 * Bewusst hier und nicht je Schicht: dieselbe Umdeutung entstuende sonst dreimal (API beim
 * Annehmen, Oberflaeche beim Bearbeiten, Angebotsdokument beim Drucken). Genau das ist am
 * 17.09.2026 schon einmal passiert — `alsSektionsModus` lag als Kopie in drei Schichten, und eine
 * davon verschluckte `total_event`.
 */
export {
  kanalVerfuegbar,
  grundWarumNichtVerfuegbar,
  sofortMoeglich,
  istVollanzahlung,
  pruefeVereinbarung,
  alsZahlungsvereinbarung,
  anzahlungFaelligAm,
  restzahlungFaelligAm,
  RESTZAHLUNG_STANDARD_TAGE_VOR,
  RESTZAHLUNG_STANDARD_TAGE_NACH,
} from "./zahlungsvereinbarung";
export type {
  Kanal,
  RestKanal,
  AnzahlungsFrist,
  RestzahlungsFrist,
  Anzahlung,
  Restzahlung,
  Zahlungsvereinbarung,
  Bereitschaft,
  Befund,
  AlteKonditionen,
  Uebersetzung,
  Fristangaben,
} from "./zahlungsvereinbarung";

/*
 * Abrechnung nach der Veranstaltung (Konzept 28.09.2026, Stufe 1): geordnete Ist-Zeilen, Rabatt mit
 * Bezug, Mehr-/Minderverbrauch, Aufteilung je Steuersatz, Saldo gegen Anzahlungen und Zahlungen.
 */
export { berechneAbrechnung, teileSpeisenGetraenke, abrechnungsSaldo, AbrechnungsFehler } from "./abrechnung";
export type {
  AbrechnungsZeile,
  AbrechnungsZeilenArt,
  RabattBezug,
  AbrechnungsErgebnis,
  ZeilenErgebnis,
  SatzBetrag,
  Saldo,
  AbrechnungsFehlerCode,
} from "./abrechnung";

/** Betrag je Gast (Buchungsstrecke B2/B3) — eine Stelle, Details in `pro-person.ts`. */
export { proPersonCents } from "./pro-person";
/*
 * Buchungsstrecke D4/B3 — Preisrahmen „48 € bis 72 € pro Person“ aus persistierten Beträgen
 * (Menüpakete des Katalogs, später die Varianten eines Angebots). Details in `spanne.ts`.
 */
export { preisSpanne, menuePreisSpanne, euroKurz, spanneKurz, menueSpanneSatz, variantenRahmen, variantenRahmenText } from "./spanne";
export type { PreisSpanne, MenuePaket, VarianteBetrag, VariantenRahmen } from "./spanne";

/* Pakete-Widget P1 — ab wann ein Paket auf die Website darf, und sein Preistext. Details in `paket-website.ts`. */
export {
  WEBSITE_UEBERSETZUNGS_SPRACHEN,
  fehlendeWebsiteSprachen,
  enthaeltPreisangabe,
  PREIS_SPRUNG_SCHWELLE,
  preisSprung,
  istPreisSprung,
  proPersonPreisText,
  paketWebsitePreisText,
  paketWebsiteMaxGaesteText,
  kategorieAbPreis,
  speisekartePreisText,
  speisekarteAbPreis,
} from "./paket-website";
export type { WebsiteUebersetzungsSprache, PaketUebersetzungen } from "./paket-website";

/* E3 (08.10.2026): Anzahlung je USt-Satz, Netto/MwSt/Brutto — Beleg und Gastseite aus einer Stelle. */
export { splitDepositByVat, anzahlungAufschluesselung } from "./anzahlung-ust";
export type { VatGrossBucket, AnzahlungSatz, AnzahlungAufschluesselung } from "./anzahlung-ust";

/** Nachtrag-Version (docs/PLAN-NACHTRAG-VERSION.md, Einheit 2): Zahlungsausgleich und Vergleich N -> N+1. */
export { nachtragsAusgleich, nachtragsVergleich, nachtragAnzahlungSollCents } from "./nachtrag";
export type {
  NachtragKonditionen,
  NachtragZahlung,
  NachtragsAusgleichEingabe,
  NachtragsAusgleich,
  NachtragsAusgleichFehler,
  NachtragAktion,
  NachtragZahlungsAktion,
  NachtragPositionSnapshot,
  NachtragOptionSnapshot,
  NachtragEventSnapshot,
  NachtragStand,
  PositionsStand,
  PositionsAenderung,
  NachtragsVergleich,
} from "./nachtrag";

/** sql/771 (Menuewahl im Paket): Betrag und Ausgleich nach der Buchung. Verzeichnis und Aufloesung stehen oben in dieser Datei. */
export { menuewahlBetrag, menuewahlAusgleich } from "./menuewahl";
export type { MenuewahlBetrag, MenuewahlAusgleichEingabe, MenuewahlAusgleich } from "./menuewahl";
